import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React from "react";
import {
  Button,
  Alert,
  Intent,
  Dialog,
  HTMLSelect,
  InputGroup,
  Classes,
  Spinner,
  Popover,
  Position,
  Menu,
  MenuItem,
  MenuDivider
} from "@blueprintjs/core";
import { noop } from "lodash-es";
import axios from "axios";
import { anyToJson, convertBasePosTraceToPerBpTrace } from "@teselagen/bio-parsers";
import MonacoYamlEditor from "./MonacoYamlEditor";
import GraphViewer from "./GraphViewer";
import store from "./store";
import { addAlignment, AlignmentView, actions as oveActions } from "../../src/";
import {
  getSequenceDataBetweenRange,
  findSequenceMatches,
  getReverseComplementSequenceString
} from "@teselagen/sequence-utils";
import { autoAnnotateFeatures, autoAnnotateParts, autoAnnotatePrimers, FolderPicker } from "../../src/AutoAnnotate";
import ProjectFilesTableView from "./ProjectFilesTableView";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "../../src/Reflex";
import "../../src/Reflex/reflex-styles.css";
import { getErrorMessage } from "./utils/errorUtils";
import { getAssemblyScaffold } from "./utils/assemblyScaffold";
import BackgroundBuildIndicator from "./BackgroundBuildIndicator";
const SEQUENCE_EXTENSIONS = [
  ".gen.gb",
  ".gb",
  ".gbk",
  ".genbank",
  ".fasta",
  ".fa",
  ".assembly"
];
const isFile = (path) => {
  if (!path) return false;
  const low = path.toLowerCase();
  return SEQUENCE_EXTENSIONS.some((ext) => low.endsWith(ext)) || low.endsWith(".sam.json") || low.endsWith(".jbrowse.zip") || low.endsWith(".ab1");
};
const updateAssemblyInventoryStatusInText = (content, inInventory) => {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  let found = false;
  const pattern = /^(\s*)in_inventory\s*:\s*(.*)$/i;
  for (let i = 0; i < lines.length; i++) {
    const match = lines[i].match(pattern);
    if (match) {
      const indent = match[1];
      lines[i] = `${indent}in_inventory: ${inInventory ? "true" : "false"}`;
      found = true;
      break;
    }
  }
  if (found) {
    return lines.join("\n");
  } else {
    if (content && !content.endsWith("\n")) {
      return content + `
in_inventory: ${inInventory ? "true" : "false"}
`;
    } else {
      return content + `in_inventory: ${inInventory ? "true" : "false"}
`;
    }
  }
};
const updateAssemblyCommentsInText = (content, comments) => {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  let startIdx = -1;
  let endIdx = -1;
  let indent = "";
  const commentsPattern = /^(\s*)comments\s*:\s*(.*)$/i;
  for (let i = 0; i < lines.length; i++) {
    const match = lines[i].match(commentsPattern);
    if (match) {
      startIdx = i;
      indent = match[1];
      let j = i + 1;
      while (j < lines.length) {
        const nextLine = lines[j];
        if (!nextLine.trim()) {
          let lookAhead = j + 1;
          let belongs = false;
          while (lookAhead < lines.length) {
            const laLine = lines[lookAhead];
            if (!laLine.trim()) {
              lookAhead++;
              continue;
            }
            const laMatch = laLine.match(/^(\s*)-\s*(.*)$/);
            if (laMatch && laMatch[1].length > indent.length) {
              belongs = true;
            }
            break;
          }
          if (belongs) {
            j = lookAhead;
            continue;
          } else {
            break;
          }
        }
        const nextMatch = nextLine.match(/^(\s*)-\s*(.*)$/);
        if (nextMatch) {
          const itemIndent = nextMatch[1];
          if (itemIndent.length > indent.length) {
            j++;
            continue;
          }
        }
        if (nextLine.trim().startsWith("#")) {
          const lineIndent = nextLine.match(/^(\s*)/)[1];
          if (lineIndent.length > indent.length) {
            j++;
            continue;
          }
        }
        break;
      }
      endIdx = j;
      break;
    }
  }
  const newCommentsLines = [];
  if (comments && comments.length > 0) {
    newCommentsLines.push(`${indent}comments:`);
    comments.forEach((c) => {
      const escaped = c.replace(/'/g, "''");
      newCommentsLines.push(`${indent}  - '${escaped}'`);
    });
  }
  let resultLines = [];
  if (startIdx !== -1) {
    resultLines = [...lines.slice(0, startIdx), ...newCommentsLines, ...lines.slice(endIdx)];
  } else {
    if (comments && comments.length > 0) {
      resultLines = [...lines];
      let insertPos = resultLines.length;
      while (insertPos > 0 && !resultLines[insertPos - 1].trim()) {
        insertPos--;
      }
      resultLines.splice(insertPos, 0, ...newCommentsLines);
    } else {
      resultLines = [...lines];
    }
  }
  return resultLines.join("\n");
};
export default class StandaloneDemo extends React.Component {
  constructor(props) {
    super(props);
    const hash = window.location.hash;
    const search = window.location.search;
    let filePath = null;
    let viewMode = "editor";
    if (hash.includes("?")) {
      const urlParams = new URLSearchParams(hash.split("?")[1]);
      filePath = urlParams.get("file");
      if (urlParams.get("view") === "graph") {
        viewMode = "graph";
      } else if (urlParams.get("view") === "table") {
        viewMode = "table";
      }
    } else if (search.includes("file=")) {
      const urlParams = new URLSearchParams(search);
      filePath = urlParams.get("file");
    }
    if (!filePath && (hash === "#/Standalone" || !hash || hash === "#/")) {
      viewMode = "recent";
    }
    const savedSize = localStorage.getItem("lessonsLearnedDialogSize");
    let initialSize = { width: "80%", height: "auto" };
    if (savedSize) {
      try {
        initialSize = JSON.parse(savedSize);
      } catch (e) {
        console.error("Failed to parse saved dialog size", e);
      }
    }
    const savedPrimersSize = localStorage.getItem(
      "verificationPrimersDialogSize"
    );
    let initialPrimersSize = { width: "90%", height: "70%" };
    if (savedPrimersSize) {
      try {
        initialPrimersSize = JSON.parse(savedPrimersSize);
      } catch (e) {
        console.error("Failed to parse saved primers dialog size", e);
      }
    }
    const savedRtQpcrPrimersSize = localStorage.getItem(
      "rtQpcrPrimersDialogSize"
    );
    let initialRtQpcrPrimersSize = { width: "90%", height: "70%" };
    if (savedRtQpcrPrimersSize) {
      try {
        initialRtQpcrPrimersSize = JSON.parse(savedRtQpcrPrimersSize);
      } catch (e) {
        console.error("Failed to parse saved RT-qPCR primers dialog size", e);
      }
    }
    this.state = {
      registryHost: "http://localhost/registry",
      // Default
      currentFilePath: filePath,
      currentPrimerPath: null,
      viewMode,
      showSettings: false,
      lastServerTimestamp: null,
      isReloadAlertOpen: false,
      isGenGbSaveWarningOpen: false,
      newServerTimestamp: null,
      isSaveAsDialogOpen: false,
      saveAsFilename: "",
      saveAsFolder: "",
      availableFolders: [],
      availableFiles: null,
      boundSequences: [],
      fileHistory: [],
      isUnsavedAlertOpen: false,
      isLoading: false,
      isSaving: false,
      isSavingAs: false,
      isBuildOutputDialogOpen: false,
      buildOutput: "",
      alignmentOutput: "",
      samJsonId: sessionStorage.getItem("ove_last_loaded_file") === filePath ? sessionStorage.getItem("ove_last_sam_json_id") : null,
      jbrowseBundleId: sessionStorage.getItem("ove_last_loaded_file") === filePath ? sessionStorage.getItem("ove_last_jbrowse_bundle_id") : null,
      yamlContent: sessionStorage.getItem("ove_last_loaded_file") === filePath ? sessionStorage.getItem("ove_last_yaml_content") || "" : "",
      editorType: sessionStorage.getItem("ove_last_loaded_file") === filePath ? sessionStorage.getItem("ove_last_editor_type") || (filePath && filePath.endsWith(".sam.json") ? "samJson" : filePath && filePath.endsWith(".assembly") ? "yaml" : filePath && filePath.endsWith(".jbrowse.zip") ? "jbrowse" : "vector") : filePath && filePath.endsWith(".sam.json") ? "samJson" : filePath && filePath.endsWith(".assembly") ? "yaml" : filePath && filePath.endsWith(".jbrowse.zip") ? "jbrowse" : "vector",
      isLessonsLearnedDialogOpen: false,
      lessonsLearnedOutput: "",
      isCheckingLessonsLearned: false,
      pendingMoveCallback: null,
      lessonsLearnedDialogSize: initialSize,
      verificationPrimersDialogSize: initialPrimersSize,
      rtQpcrPrimersDialogSize: initialRtQpcrPrimersSize,
      currentCheckerHash: null,
      isExtractDialogOpen: false,
      extractFilename: "",
      extractFolder: "",
      extractStart: 0,
      extractEnd: 0,
      isExtracting: false,
      isSaveAsPrimerDialogOpen: false,
      saveAsPrimerFilename: "",
      saveAsPrimerFolder: "",
      lastUsedPrimerFolder: "shared_parts/primers",
      saveAsPrimerSequence: "",
      isSavingAsPrimer: false,
      pendingAction: null,
      currentFileHash: null,
      isVerificationPrimersDialogOpen: false,
      verificationPrimersOutput: "",
      isGeneratingVerificationPrimers: false,
      isRtQpcrPrimersDialogOpen: false,
      rtQpcrPrimersOutput: "",
      isGeneratingRtQpcrPrimers: false,
      isRegistering: false,
      isRegisterConfirmOpen: false,
      isIdTakenAlertOpen: false,
      attemptedId: null,
      backendDown: false,
      gitStatus: null,
      previewFlex: localStorage.getItem("ove_preview_flex") ? parseFloat(localStorage.getItem("ove_preview_flex")) : 0.5
    };
    this.isSaving = false;
    this.lessonsLearnedDialogRef = React.createRef();
    this.verificationPrimersDialogRef = React.createRef();
    this.rtQpcrPrimersDialogRef = React.createRef();
    this.extractInputRef = React.createRef();
    this.resizeObserver = null;
  }
  shouldSuppressReload = () => {
    const hasReloadFalse = window.location.href.includes("reload=false");
    const hasSuppressReloadSet = localStorage.getItem("suppressReload") === "true";
    return hasReloadFalse || hasSuppressReloadSet;
  };
  componentDidMount() {
    this.mountEditor();
    const searchParams = new URLSearchParams(window.location.search);
    const errorParam = searchParams.get("error");
    if (errorParam) {
      setTimeout(() => {
        window.toastr.error(errorParam);
      }, 500);
      searchParams.delete("error");
      const newSearch = searchParams.toString();
      const newUrl = window.location.pathname + (newSearch ? "?" + newSearch : "") + window.location.hash;
      window.history.replaceState(null, "", newUrl);
    }
    axios.get("/api/config").then((res) => {
      if (res.data && res.data.registry_host) {
        console.log(`[StandaloneDemo] Using registry: ${res.data.registry_host}`);
        this.setState({ registryHost: res.data.registry_host });
      }
    }).catch((err) => {
      console.error("Failed to fetch config from backend", err);
    });
    const suppressReload = this.shouldSuppressReload();
    if (!window.__OVE_INITIAL_LOAD_DONE__) {
      this.handleUrlChange(null, true);
      window.__OVE_INITIAL_LOAD_DONE__ = true;
    } else {
      this.handleUrlChange(null, true);
    }
    window.addEventListener("hashchange", this.handleUrlChange);
    this.pollInterval = setInterval(this.pollFileChanges, 5e3);
    window.addEventListener("file-reverted", this.handleFileRevert);
    window.addEventListener("file-saving", this.handleFileSaving);
    window.addEventListener("file-saved", this.handleFileSaved);
    window.addEventListener("file-save-failed", this.handleFileSaveFailed);
    window.addEventListener("file-move-check", this.handleFileMoveCheck);
    window.addEventListener("file-inventory-toggling", this.handleFileInventoryToggling);
    window.addEventListener("file-comments-updating", this.handleFileCommentsUpdating);
    window.addEventListener("file-tree-updated", this.handleFileTreeUpdated);
    window.addEventListener("run-assembly-build", this.handleRunBuild);
    window.addEventListener("open-build-output-dialog", this.handleOpenBuildOutputDialog);
    window.addEventListener("save-as-primer", this.handleSaveAsPrimerEvent);
    window.addEventListener("keydown", this.handleGlobalKeyDown, true);
    this.fetchCheckerInfo();
    this.checkBackendHeartbeat();
  }
  fetchCheckerInfo = async () => {
    try {
      const response = await axios.get("/api/lessons_learned/info");
      this.setState({ currentCheckerHash: response.data.checker_hash });
    } catch (error) {
      console.error("Error fetching checker info:", error);
    }
  };
  checkBackendHeartbeat = async () => {
    try {
      await axios.get("/api/heartbeat", { timeout: 5e3 });
    } catch (error) {
      console.error("[OVE] Backend heartbeat failed:", error);
      this.setState({ backendDown: true });
    }
  };
  componentWillUnmount() {
    this.unmounted = true;
    window.removeEventListener("hashchange", this.handleUrlChange);
    window.removeEventListener("file-reverted", this.handleFileRevert);
    window.removeEventListener("file-saving", this.handleFileSaving);
    window.removeEventListener("file-saved", this.handleFileSaved);
    window.removeEventListener("file-save-failed", this.handleFileSaveFailed);
    window.removeEventListener("file-move-check", this.handleFileMoveCheck);
    window.removeEventListener("file-inventory-toggling", this.handleFileInventoryToggling);
    window.removeEventListener("file-comments-updating", this.handleFileCommentsUpdating);
    window.removeEventListener("file-tree-updated", this.handleFileTreeUpdated);
    window.removeEventListener("run-assembly-build", this.handleRunBuild);
    window.removeEventListener("open-build-output-dialog", this.handleOpenBuildOutputDialog);
    window.removeEventListener("save-as-primer", this.handleSaveAsPrimerEvent);
    window.removeEventListener("keydown", this.handleGlobalKeyDown, true);
    this.editor = null;
    if (this.pollInterval) clearInterval(this.pollInterval);
    if (this.updateFilenameTimeout) clearTimeout(this.updateFilenameTimeout);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    document.title = "OVE";
  }
  handleGlobalKeyDown = (e) => {
    const openDialog = document.querySelector(".bp3-overlay-open .bp3-dialog") || document.querySelector(".bp3-overlay-open .bp3-alert") || Array.from(document.querySelectorAll(".bp3-dialog")).find(
      (d) => d.offsetParent !== null
    );
    const isTableVisible = this.state.viewMode === "table" || this.state.viewMode === "recent" || this.state.viewMode === "bound-sequences";
    if (isTableVisible && !openDialog && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f") {
      const previewPane = document.querySelector('[data-testid="preview-pane"]');
      const isFocusInPreview = previewPane && previewPane.contains(document.activeElement);
      if (!isFocusInPreview) {
        const searchInput = document.getElementById("table-search-input") || document.querySelector('.project-files-table-view input[placeholder="Search..."]') || document.querySelector('input[placeholder="Search..."]');
        if (searchInput) {
          e.preventDefault();
          e.stopPropagation();
          searchInput.focus();
          searchInput.select();
          return;
        }
      }
    }
    if (openDialog && (e.ctrlKey || e.metaKey)) {
      const key = e.key.toLowerCase();
      if (key === "f" || key === "a" || key === "c") {
        const isTargetInDialog = openDialog.contains(e.target);
        const isSelectionInDialog = window.getSelection()?.anchorNode && openDialog.contains(window.getSelection().anchorNode);
        if (isTargetInDialog || isSelectionInDialog) {
          e.stopPropagation();
          return;
        }
      }
    }
    if (e.altKey && (e.key?.toLowerCase() === "k" || e.code === "KeyK")) {
      e.preventDefault();
      e.stopPropagation();
      const showFilesBtn = Array.from(
        document.querySelectorAll("button, a")
      ).find((b) => b.textContent?.includes("Show Project Files"));
      if (showFilesBtn) {
        showFilesBtn.click();
      }
      const focusSearch = () => {
        const input = document.querySelector(
          '[data-testid="search-input"] input, [data-testid="search-input"], #table-search-input, .ve-file-explorer-search input, input[placeholder*="Search by name/desc/DNA"], input[placeholder="Search..."]'
        );
        if (input) {
          input.focus();
          if (input.select) input.select();
          return true;
        }
        return false;
      };
      if (!focusSearch()) {
        [20, 60, 120, 250, 400].forEach((ms) => setTimeout(focusSearch, ms));
      }
      return;
    }
    if (e.altKey && e.key.toLowerCase() === "c") {
      if (openDialog && openDialog.querySelector(".bp3-icon-git-commit")) {
        const dialogCommitBtn = openDialog.querySelector(
          ".bp3-dialog-footer .bp3-intent-primary"
        );
        if (dialogCommitBtn && !dialogCommitBtn.disabled) {
          e.preventDefault();
          e.stopPropagation();
          dialogCommitBtn.click();
          return;
        }
      }
      if (!openDialog) {
        const sidebarCommitBtn = document.querySelector(".bp3-icon-git-commit")?.closest('button[title*="Git Commit"]');
        if (sidebarCommitBtn && !sidebarCommitBtn.disabled) {
          e.preventDefault();
          e.stopPropagation();
          sidebarCommitBtn.click();
          return;
        }
      }
    }
    if (e.altKey && (e.key?.toLowerCase() === "n" || e.code === "KeyN")) {
      e.preventDefault();
      e.stopPropagation();
      const showFilesBtn = Array.from(
        document.querySelectorAll("button, a")
      ).find((b) => b.textContent?.includes("Show Project Files"));
      if (showFilesBtn) {
        showFilesBtn.click();
        [20, 60, 120, 250, 400].forEach(
          (ms) => setTimeout(() => {
            window.dispatchEvent(new CustomEvent("open-new-item-dialog"));
          }, ms)
        );
      } else {
        window.dispatchEvent(new CustomEvent("open-new-item-dialog"));
      }
      return;
    }
    if (e.key === "Enter" || e.key === "Escape") {
      if (!openDialog) {
        if (e.key === "Escape") {
          const minimizeBtn = document.querySelector(".bp3-icon-minimize")?.closest("button") || document.querySelector('[icon="minimize"]')?.closest("button");
          if (minimizeBtn) {
            e.preventDefault();
            e.stopPropagation();
            minimizeBtn.click();
            return;
          }
        }
        if (e.key === "Escape" && this.state.editorType === "vector") {
          store.dispatch(oveActions.selectionLayerClear(void 0, { editorName: "Standalone" }));
          store.dispatch(oveActions.caretPositionUpdate(-1, { editorName: "Standalone" }));
          const state = store.getState();
          const editorState = state.VectorEditor.Standalone;
          if (editorState && editorState.findTool && editorState.findTool.isOpen) {
            store.dispatch(oveActions.toggleFindTool(void 0, { editorName: "Standalone" }));
          }
        }
        return;
      }
      if (e.key === "Enter") {
        const activeTag = document.activeElement?.tagName.toLowerCase();
        if (activeTag === "input" || activeTag === "textarea") return;
        const confirmBtn = openDialog.querySelector(".bp3-button.bp3-intent-danger") || openDialog.querySelector(".bp3-button.bp3-intent-primary") || openDialog.querySelector(".bp3-button.bp3-intent-success");
        if (confirmBtn) {
          e.preventDefault();
          e.stopPropagation();
          confirmBtn.click();
          return;
        }
      }
      const closeX = openDialog.querySelector(".bp3-dialog-close-button");
      if (closeX) {
        e.preventDefault();
        e.stopPropagation();
        closeX.click();
        return;
      }
      const closeBtn = Array.from(
        openDialog.querySelectorAll(".bp3-button")
      ).find((b) => {
        const text = b.textContent?.trim().toLowerCase();
        return text === "close" || text === "cancel";
      });
      if (closeBtn) {
        e.preventDefault();
        e.stopPropagation();
        closeBtn.click();
        return;
      }
    }
  };
  handleFileSaving = (e) => {
    if (e.detail.path === this.state.currentFilePath) {
      this.isSaving = true;
    }
  };
  handleFileSaved = (e) => {
    const detail = e?.detail;
    if (detail && detail.path === this.state.currentFilePath) {
      this.isSaving = false;
      const update = { lastServerTimestamp: detail.last_modified };
      if (detail.description !== void 0) {
        update.currentDescription = detail.description;
      }
      if (this.state.isReloadAlertOpen && this.state.newServerTimestamp === detail.last_modified) {
        update.isReloadAlertOpen = false;
        update.newServerTimestamp = null;
      }
      this.setState(update);
      if (detail.description !== void 0) {
        const filename = detail.path.split("/").pop();
        this.updateFilenameDisplay(filename, detail.description);
        if (this.state.editorType === "vector" && this.editor) {
          const state = this.editor.getState();
          this.editor.updateEditor({
            sequenceData: {
              ...state.sequenceData,
              description: detail.description
            }
          });
        }
      }
    }
    if (this.state.editorType === "yaml") {
      this.fetchFolders();
    }
  };
  handleFileInventoryToggling = (e) => {
    const { path, in_inventory } = e.detail;
    const { currentFilePath, editorType, yamlContent } = this.state;
    if (currentFilePath && currentFilePath === path && editorType === "yaml" && !this.isDirty()) {
      const updatedContent = updateAssemblyInventoryStatusInText(yamlContent || "", in_inventory);
      this.setState({
        yamlContent: updatedContent
      });
      sessionStorage.setItem("ove_last_yaml_content", updatedContent);
    }
  };
  handleFileCommentsUpdating = (e) => {
    const { path, comments } = e.detail;
    const { currentFilePath, editorType, yamlContent } = this.state;
    if (currentFilePath && currentFilePath === path && editorType === "yaml" && !this.isDirty()) {
      const updatedContent = updateAssemblyCommentsInText(yamlContent || "", comments);
      this.setState({
        yamlContent: updatedContent
      });
      sessionStorage.setItem("ove_last_yaml_content", updatedContent);
    }
  };
  handleFileSaveFailed = (e) => {
    if (e.detail.path === this.state.currentFilePath) {
      this.isSaving = false;
      this.handleUrlChange(null, true);
    }
  };
  handleFileRevert = (e) => {
    const { path, build_output } = e.detail;
    const { currentFilePath } = this.state;
    if (currentFilePath && (currentFilePath === path || currentFilePath.startsWith(path + "/"))) {
      this.isSaving = false;
      this.setState({
        isReloadAlertOpen: false,
        isUnsavedAlertOpen: false,
        buildOutput: build_output || "",
        isBuildOutputDialogOpen: !e.detail.squelchPopup && !!(build_output || e.detail.alignment_output),
        alignmentOutput: e.detail.alignment_output || "",
        lessonsLearnedCache: {
          ...this.state.lessonsLearnedCache,
          [currentFilePath]: null
        }
      });
      this.handleUrlChange(null, true);
    } else if (build_output && !e.detail.squelchPopup) {
      this.setState({
        buildOutput: build_output,
        alignmentOutput: e.detail.alignment_output || "",
        isBuildOutputDialogOpen: true
      });
    }
  };
  handleOpenBuildOutputDialog = (e) => {
    const detail = e?.detail || {};
    let buildOutput = detail.build_output || "";
    if (detail.error && !buildOutput) {
      buildOutput = `Error: ${detail.error}`;
    }
    this.setState({
      buildOutput,
      alignmentOutput: detail.alignment_output || "",
      isBuildOutputDialogOpen: true
    });
  };
  isDirty = () => {
    if (this.state.isNewFile) return true;
    if (this.state.editorType === "vector") {
      if (this.editor) {
        const state = this.editor.getState();
        if (state) {
          const { sequenceData, lastSavedId } = state;
          return !!(sequenceData?.stateTrackingId && sequenceData.stateTrackingId !== lastSavedId);
        }
      }
    } else if (this.state.editorType === "yaml") {
      if (this.yamlEditor) {
        return this.yamlEditor.getIsDirty?.();
      }
    }
    return false;
  };
  handleFileMoveCheck = (e) => {
    const { path, callback } = e.detail;
    const { currentFilePath } = this.state;
    if (currentFilePath && (currentFilePath === path || currentFilePath.startsWith(path + "/"))) {
      if (this.isDirty()) {
        this.setState({
          isUnsavedAlertOpen: true,
          pendingMoveCallback: callback
        });
      } else {
        callback();
      }
    } else {
      callback();
    }
  };
  handleFileTreeUpdated = (e) => {
    this.processFileTree(e.detail);
  };
  processFileTree = (data) => {
    const folders = [];
    const files = [];
    const treeData = data.files || data;
    const traverse = (node) => {
      if (node.type === "directory") {
        folders.push({
          path: node.path === "." ? "" : node.path,
          modifiedDate: node.modifiedDate || null
        });
        if (node.children) {
          node.children.forEach((child) => traverse(child));
        }
      } else if (node.type === "file") {
        if (SEQUENCE_EXTENSIONS.some((ex) => node.name.endsWith(ex)) || node.name.endsWith(".assembly") || node.name.endsWith(".sam.json") || node.name.endsWith(".jbrowse.zip") || node.name.endsWith(".ab1")) {
          files.push({
            name: node.name,
            path: node.path,
            isSymlink: node.isSymlink,
            symlinkTarget: node.symlinkTarget,
            circular: node.circular,
            description: node.description,
            isNewUnsaved: node.isNewUnsaved,
            inInventory: node.inInventory !== void 0 ? node.inInventory : node.in_inventory !== void 0 ? node.in_inventory : false,
            hasComments: node.hasComments || false,
            modifiedDate: node.modifiedDate
          });
        }
      }
    };
    traverse(treeData);
    this.setState({
      availableFolders: folders.map((f) => f.path),
      availableFiles: files,
      gitStatus: data.git_status || {}
    });
  };
  pollFileChanges = async () => {
    const hash = window.location.hash;
    let viewModeFromHash = "editor";
    if (hash.includes("view=table")) viewModeFromHash = "table";
    if (hash.includes("view=graph")) viewModeFromHash = "graph";
    const {
      currentFilePath,
      lastServerTimestamp,
      isReloadAlertOpen,
      isNewFile
    } = this.state;
    if (!currentFilePath || isReloadAlertOpen || this.isSaving || isNewFile || viewModeFromHash === "table" || window.isInventoryToggling || window.isReloadingFile) {
      return;
    }
    try {
      const encodedPath = currentFilePath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
      const response = await axios.get(
        `/api/file/metadata/${encodedPath}?t=${Date.now()}`
      );
      const { last_modified, file_hash } = response.data;
      if (window.isInventoryToggling || window.isReloadingFile || this.isSaving || this.state.isReloadAlertOpen || this.state.currentFilePath !== currentFilePath) {
        return;
      }
      const isHashUnchanged = file_hash && this.state.currentFileHash && file_hash === this.state.currentFileHash;
      if (lastServerTimestamp && last_modified > lastServerTimestamp && !isHashUnchanged) {
        this.setState({
          isReloadAlertOpen: true,
          newServerTimestamp: last_modified,
          newServerHash: file_hash,
          newServerDescription: response.data.description
        });
      } else {
        const update = {};
        if (last_modified && last_modified !== this.state.lastServerTimestamp) {
          update.lastServerTimestamp = last_modified;
        }
        if (file_hash !== this.state.currentFileHash) {
          update.currentFileHash = file_hash;
        }
        if (response.data.description !== void 0 && response.data.description !== this.state.currentDescription) {
          update.currentDescription = response.data.description;
        }
        const isAssembly = currentFilePath.toLowerCase().endsWith(".assembly");
        if (isAssembly && response.data.in_inventory !== void 0 && !this.isDirty()) {
          const currentContent = this.state.yamlContent || "";
          const updatedContent = updateAssemblyInventoryStatusInText(
            currentContent,
            response.data.in_inventory
          );
          if (updatedContent !== currentContent) {
            update.yamlContent = updatedContent;
            sessionStorage.setItem("ove_last_yaml_content", updatedContent);
          }
        }
        if (Object.keys(update).length > 0) {
          this.setState(update);
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        this.setState({
          currentFilePath: null,
          lastServerTimestamp: null
        });
        window.location.hash = this.getHashUrl(null);
      } else {
        console.error("Error polling file changes:", error);
      }
    }
  };
  handleReloadConfirm = async () => {
    const { newServerTimestamp, newServerHash } = this.state;
    this.setState({
      lastServerTimestamp: newServerTimestamp,
      currentFileHash: newServerHash,
      currentDescription: this.state.newServerDescription || this.state.currentDescription,
      isReloadAlertOpen: false,
      newServerTimestamp: null,
      newServerHash: null,
      newServerDescription: null
    });
    const newCache = { ...this.getLessonsLearnedCache() };
    if (this.state.currentFilePath) {
      delete newCache[this.state.currentFilePath];
      this.setLessonsLearnedCache(newCache);
    }
    await this.handleUrlChange(null, true);
  };
  handleReloadCancel = () => {
    const { newServerTimestamp } = this.state;
    this.setState({
      lastServerTimestamp: newServerTimestamp,
      isReloadAlertOpen: false,
      newServerTimestamp: null
    });
  };
  componentDidUpdate(prevProps, prevState) {
    if (prevState.currentFilePath !== this.state.currentFilePath || prevState.isReadOnly !== this.state.isReadOnly || prevState.editorType !== this.state.editorType || prevState.backendDown !== this.state.backendDown || prevState.viewMode !== this.state.viewMode) {
      this.mountEditor();
    }
  }
  onPreview = (filePath, location) => {
    if (this.state.currentFilePath === filePath && this.state.currentLocation === location) {
      if (this.state.viewMode === "bound-sequences" || this.state.viewMode === "recent") {
        window.location.hash = this.getHashUrl(null, {
          view: this.state.viewMode
        });
      } else {
        const parts = filePath.split("/");
        parts.pop();
        const folderPath = parts.join("/") || "projects";
        window.location.hash = this.getHashUrl(folderPath, {
          view: this.state.viewMode
        });
      }
    } else {
      const extraParams = { view: this.state.viewMode };
      if (location) {
        extraParams.location = location;
      }
      window.location.hash = this.getHashUrl(filePath, extraParams);
    }
  };
  mountEditor = (filePathOverride) => {
    const node = this.node;
    if (!node) {
      if (this.editor) {
        const editorToClose = this.editor;
        this.editor = null;
        setTimeout(() => editorToClose.close(), 0);
      }
      return;
    }
    const filePath = filePathOverride || this.state.currentFilePath;
    const isGenGb = filePath && filePath.endsWith(".gen.gb");
    const isLocked = this.state.isReadOnly || false;
    if (this.editor) {
      if (this.currentFilePath === filePath && this.currentIsLocked === isLocked && this.editorNode === node) {
        return;
      }
    }
    this.editorNode = node;
    this.currentFilePath = filePath;
    this.currentIsLocked = isLocked;
    this.currentIsGenGb = isGenGb;
    try {
      this.editor = window.createVectorEditor(node, {
        shouldAutosave: false,
        showAvailability: true,
        contentLeft: /* @__PURE__ */ jsx(
          Button,
          {
            minimal: true,
            icon: "share",
            title: "Copy URL",
            onClick: () => {
              const url = window.location.origin + window.location.pathname + window.location.hash;
              navigator.clipboard?.writeText(url);
              window.toastr?.success("URL copied to clipboard");
            }
          }
        ),
        onRename: noop,
        onNew: noop,
        onDuplicate: noop,
        onSave: this.handleSave,
        onSaveAs: this.handleSaveAsOve,
        onDelete: noop,
        onCopy: (event, data) => {
          const clipboardData = event.clipboardData;
          clipboardData.setData("application/json", JSON.stringify(data));
          event.preventDefault();
        },
        onPaste: (event) => {
          const clipboardData = event.clipboardData;
          const jsonData = clipboardData.getData("application/json");
          return jsonData ? JSON.parse(jsonData) : { sequence: clipboardData.getData("text/plain") };
        },
        onBeforeExport: this.handleBeforeExport,
        showMenuBar: true,
        editorName: filePath,
        // readOnly: ..., // Removed to let Redux state control this
        disableSetReadOnly: this.state.isReadOnly || false,
        // .gen.gb files are read-only, but the Save button/Ctrl+S must still reach
        // handleSave so it can show the "generated file" warning instead of silently
        // no-opping (withEditorProps' handleSave gates on readOnly otherwise).
        alwaysAllowSave: isGenGb,
        sequenceData: this.pendingFileData && this.pendingFileData.sequence !== void 0 ? this.pendingFileData : { sequence: "" },
        getVersionList: async () => {
          try {
            if (this.currentDocId) {
              const res = await axios.get(`/api/db/documents/${this.currentDocId}/versions`);
              return (res.data.versions || []).map((v) => ({
                versionId: v.id,
                dateChanged: v.created_at ? new Date(v.created_at).toLocaleDateString() : "Saved",
                editedBy: "Collaborator",
                revisionType: v.version_name || "Snapshot"
              }));
            }
          } catch (e) {
            console.warn("Could not fetch DB versions:", e);
          }
          return [];
        },
        getSequenceAtVersion: async (versionId) => {
          try {
            if (this.currentDocId) {
              const res = await axios.post(`/api/db/documents/${this.currentDocId}/restore/${versionId}`);
              if (res.data && res.data.content) {
                try {
                  return JSON.parse(res.data.content);
                } catch {
                  return { sequence: res.data.content };
                }
              }
            }
          } catch (e) {
            console.error("Failed to restore sequence version:", e);
          }
          return this.editor?.getState()?.sequenceData;
        },
        autoAnnotateFeatures,
        autoAnnotateParts,
        autoAnnotatePrimers,
        annotationImport: (matches) => {
          if (this.editor) {
            const state = this.editor.getState();
            const newSequenceData = {
              ...state.sequenceData,
              features: [...state.sequenceData.features || [], ...matches]
            };
            this.editor.updateEditor({
              sequenceData: newSequenceData
            });
            window.toastr.success(`Imported ${matches.length} annotations`);
          }
        },
        featureLibrary: [
          { name: "Promoter", sequence: "tatata", type: "promoter", id: "1" },
          { name: "Terminator", sequence: "aaaaaa", type: "terminator", id: "2" }
        ],
        partLibrary: [
          { name: "Part1", sequence: "ggcctt", type: "misc_feature", id: "3" }
        ],
        annotationVisibility: {
          cutsites: false,
          cutsitesInSequence: false
        },
        PropertiesProps: {
          propertiesList: [
            "general",
            "features",
            "parts",
            "primers",
            "translations",
            "cutsites",
            "orfs",
            "genbank"
          ]
        },
        menuFilter: this.menuFilter,
        rightClickOverrides: {
          featureRightClicked: (items, { annotation }) => {
            const featureName = annotation.name;
            const designRtQpcrItem = {
              text: "Design RT-qPCR Primers",
              icon: "predictive-analysis",
              onClick: () => this.handleDesignRtQpcrPrimers(featureName)
            };
            const extractItem = {
              text: "Extract Sequence",
              icon: "export",
              onClick: () => this.handleExtractSelection(
                annotation.start,
                annotation.end,
                annotation.name
              )
            };
            const isPrimerLike = annotation.type && (annotation.type.toLowerCase().includes("primer") || annotation.type === "oligo");
            let savePrimerItem = null;
            if (isPrimerLike) {
              savePrimerItem = {
                text: "Save Primer",
                icon: "save",
                onClick: () => {
                  const sequenceData = this.editor.getState().sequenceData;
                  const featureSeqData = getSequenceDataBetweenRange(
                    sequenceData,
                    annotation
                  );
                  let seq = featureSeqData.sequence;
                  if (annotation.forward === false) {
                    seq = getReverseComplementSequenceString(seq);
                  }
                  this.handleSaveAsPrimer(seq, annotation.name);
                }
              };
            }
            let ceIndex = -1;
            items.forEach((item, idx) => {
              const text = item && item.text && item.text.toLowerCase();
              if (text === "create & export") {
                ceIndex = idx;
              }
            });
            let newItems = [...items];
            if (ceIndex !== -1) {
              newItems.splice(ceIndex, 0, designRtQpcrItem);
            } else {
              newItems.push("--", designRtQpcrItem);
            }
            let addedExtract = false;
            let addedSave = false;
            newItems = newItems.map((item) => {
              const text = item && item.text && item.text.toLowerCase();
              if (text && text.includes("create") && text.includes("export")) {
                addedExtract = true;
                const submenu = Array.isArray(item.submenu) ? item.submenu : [];
                const finalSubmenu = [...submenu, extractItem];
                if (savePrimerItem) {
                  finalSubmenu.push(savePrimerItem);
                }
                return { ...item, submenu: finalSubmenu };
              }
              if (text === "feature management" && savePrimerItem) {
                addedSave = true;
                const submenu = Array.isArray(item.submenu) ? item.submenu : [];
                return { ...item, submenu: [...submenu, savePrimerItem] };
              }
              return item;
            });
            if (!addedExtract) {
              newItems = [...newItems, "--", extractItem];
            }
            if (!addedSave && savePrimerItem) {
              newItems.push({
                text: "Feature Management",
                submenu: [savePrimerItem]
              });
            }
            return newItems;
          },
          primerRightClicked: (items, { annotation }) => {
            const savePrimerItem = {
              text: "Save Primer",
              icon: "save",
              onClick: () => {
                let seq = annotation.bases || annotation.sequence;
                if (!seq) {
                  const sequenceData = this.editor.getState().sequenceData;
                  const featureSeqData = getSequenceDataBetweenRange(
                    sequenceData,
                    annotation
                  );
                  seq = featureSeqData.sequence;
                  if (annotation.forward === false) {
                    seq = getReverseComplementSequenceString(seq);
                  }
                }
                this.handleSaveAsPrimer(seq, annotation.name);
              }
            };
            const extractItem = {
              text: "Extract Sequence",
              icon: "export",
              onClick: () => this.handleExtractSelection(
                annotation.start,
                annotation.end,
                annotation.name
              )
            };
            let addedSave = false;
            let addedExtract = false;
            let newItems = items.map((item) => {
              const text = item && item.text && item.text.toLowerCase();
              if (text === "feature management") {
                addedSave = true;
                const submenu = Array.isArray(item.submenu) ? item.submenu : [];
                return { ...item, submenu: [...submenu, savePrimerItem] };
              }
              if (text && text.includes("create") && text.includes("export")) {
                addedExtract = true;
                const submenu = Array.isArray(item.submenu) ? item.submenu : [];
                return {
                  ...item,
                  submenu: [...submenu, extractItem, savePrimerItem]
                };
              }
              return item;
            });
            if (!addedSave) {
              newItems.push({
                text: "Feature Management",
                submenu: [savePrimerItem]
              });
            }
            if (!addedExtract) {
              newItems.push("--", extractItem);
            }
            return newItems;
          },
          partRightClicked: (items, { annotation }) => {
            const extractItem = {
              text: "Extract Sequence",
              icon: "export",
              onClick: () => this.handleExtractSelection(
                annotation.start,
                annotation.end,
                annotation.name
              )
            };
            let added = false;
            const newItems = items.map((item) => {
              const text = item && item.text && item.text.toLowerCase();
              if (text && text.includes("create") && text.includes("export")) {
                added = true;
                const submenu = Array.isArray(item.submenu) ? item.submenu : [];
                return { ...item, submenu: [...submenu, extractItem] };
              }
              return item;
            });
            return added ? newItems : [...newItems, "--", extractItem];
          },
          selectionLayerRightClicked: (items, { annotation }) => {
            const extractItem = {
              text: "Extract Sequence",
              icon: "export",
              onClick: () => this.handleExtractSelection(
                annotation.start,
                annotation.end,
                "extracted_region"
              )
            };
            let added = false;
            const newItems = items.map((item) => {
              const text = item && item.text && item.text.toLowerCase();
              if (text && text.includes("create") && text.includes("export")) {
                added = true;
                const submenu = Array.isArray(item.submenu) ? item.submenu : [];
                return { ...item, submenu: [...submenu, extractItem] };
              }
              return item;
            });
            return added ? newItems : [...newItems, "--", extractItem];
          }
        },
        ToolBarProps: {
          toolList: [
            "saveTool",
            "downloadTool",
            "importTool",
            "undoTool",
            "redoTool",
            "versionHistoryTool",
            "collabTool",
            "cutsiteTool",
            "featureTool",
            "findTool",
            "visibilityTool",
            //new
            "oligoTool",
            "orfTool",
            "partTool",
            "alignmentTool"
          ],
          additionalTopRightToolbarButtons: /* @__PURE__ */ jsxs(React.Fragment, { children: [
            this.renderRegisterSequenceButton(),
            this.renderMoreAppsDropdown()
          ] })
        }
      });
      if (this.pendingFileData) {
        this.updateEditorWithFile(this.pendingFileData, filePath, this.pendingReadOnly);
        this.pendingFileData = null;
        this.pendingReadOnly = void 0;
      }
    } catch (e) {
      console.error("[OVE] Failed to mount editor:", e);
      setTimeout(this.mountEditor, 500);
    }
  };
  updateFilenameDisplay = (filename, description) => {
    if (this.unmounted) return;
    if (typeof document === "undefined") return;
    const demoHeader = document.querySelector(".demo-header");
    if (!demoHeader) {
      this.updateFilenameTimeout = setTimeout(() => this.updateFilenameDisplay(filename, description), 100);
      return;
    }
    let moduleLink = demoHeader.querySelector(".ove-filename-display");
    if (!moduleLink) {
      moduleLink = demoHeader.querySelector('a[href*="/packages/ove"]');
      if (moduleLink) {
        moduleLink.classList.add("ove-filename-display");
      }
    }
    if (moduleLink && filename) {
      if (filename === "@teselagen/ove") {
        moduleLink.textContent = filename;
      } else {
        const displayText = description ? `<strong>${filename}</strong><span style="color: #666; font-weight: normal;"> | ${description}</span>` : `<strong>${filename}</strong>`;
        moduleLink.innerHTML = displayText;
      }
      moduleLink.style.pointerEvents = "none";
      moduleLink.style.cursor = "default";
      moduleLink.style.overflow = "hidden";
      moduleLink.style.textOverflow = "ellipsis";
      moduleLink.style.whiteSpace = "nowrap";
      moduleLink.removeAttribute("href");
    }
  };
  getHashUrl = (filePath, extraParams = {}) => {
    const searchParams = new URLSearchParams(window.location.search);
    let reload = searchParams.get("reload");
    if (!reload && window.location.hash.includes("?")) {
      const hashParams = new URLSearchParams(window.location.hash.split("?")[1]);
      reload = hashParams.get("reload");
    }
    let url = filePath ? `#/Standalone?file=${encodeURIComponent(filePath)}` : `#/Standalone`;
    if (reload) {
      url += url.includes("?") ? `&reload=${reload}` : `?reload=${reload}`;
    }
    const mergedParams = { ...extraParams };
    if (this.state.isNewFile && filePath === this.state.currentFilePath) {
      if (!("new" in mergedParams)) mergedParams.new = "true";
      if (!("type" in mergedParams) && this.state.editorType) {
        mergedParams.type = this.state.editorType === "yaml" ? "yaml" : "sequence";
      }
    }
    if (this.state.currentPrimerPath && (mergedParams.view === "bound-sequences" || !mergedParams.view && this.state.viewMode === "bound-sequences")) {
      mergedParams.primer = this.state.currentPrimerPath;
    }
    Object.entries(mergedParams).forEach(([key, value]) => {
      if (value === null) return;
      url += url.includes("?") ? `&${key}=${encodeURIComponent(value)}` : `?${key}=${encodeURIComponent(value)}`;
    });
    return url;
  };
  handleUrlChange = async (e, forceUpdate = false) => {
    const hash = window.location.hash || "";
    let filePath = null;
    let primerPath = null;
    let locationParam = null;
    let view = "editor";
    if (hash.includes("?")) {
      const urlParams = new URLSearchParams(hash.split("?")[1]);
      filePath = urlParams.get("file");
      primerPath = urlParams.get("primer");
      locationParam = urlParams.get("location");
      const viewParam = urlParams.get("view");
      if (viewParam) {
        view = viewParam;
      } else if (filePath && !isFile(filePath)) {
        view = "table";
      }
    } else if (!filePath && (hash === "#/Standalone" || hash === "#/" || !hash)) {
      view = "recent";
    }
    if (view !== this.state.viewMode || primerPath !== this.state.currentPrimerPath || locationParam !== this.state.currentLocation) {
      this.setState({ viewMode: view, currentPrimerPath: primerPath, currentLocation: locationParam });
    }
    if (filePath && (forceUpdate || filePath !== this.state.currentFilePath || primerPath !== this.state.currentPrimerPath || locationParam !== this.state.currentLocation || view === "editor" && this.state.viewMode === "graph")) {
      if (!forceUpdate && this.state.currentFilePath && view !== "graph") {
        if (this.isDirty()) {
          this.setState({
            isUnsavedAlertOpen: true,
            pendingFilePath: filePath
          });
          return;
        }
      }
      this.doHandleUrlChange(filePath, view, primerPath, locationParam);
    } else if (!filePath && this.state.currentFilePath) {
      this.setState({
        currentFilePath: null,
        currentLocation: null,
        lastServerTimestamp: null,
        yamlContent: "",
        isLoading: false
      });
      this.updateFilenameDisplay("OVE | DelveTx");
      document.title = "OVE";
      if (view === "bound-sequences" && primerPath) {
        this.doHandleUrlChange(null, view, primerPath, locationParam);
      }
    } else if (forceUpdate && this.state.currentFilePath) {
      this.doHandleUrlChange(this.state.currentFilePath, view, primerPath, locationParam);
    } else if ((view === "recent" || view === "table" || view === "bound-sequences") && !filePath) {
      this.doHandleUrlChange(null, view, primerPath, locationParam);
    }
  };
  addToHistory = (filePath) => {
    if (!filePath || filePath === "projects") return;
    try {
      const historyStr = localStorage.getItem("ove_file_history") || "[]";
      let history = JSON.parse(historyStr);
      history = history.filter((item) => item && item.path !== filePath);
      const allExtensions = [
        ...SEQUENCE_EXTENSIONS,
        ".assembly",
        ".sam.json",
        ".jbrowse.zip",
        ".ab1"
      ];
      if (!allExtensions.some((ext) => filePath.toLowerCase().endsWith(ext))) {
        return;
      }
      history.unshift({
        path: filePath,
        lastAccessed: Date.now() / 1e3
      });
      if (history.length > 15) {
        history = history.slice(0, 15);
      }
      localStorage.setItem("ove_file_history", JSON.stringify(history));
    } catch (e) {
      console.error("Error updating history:", e);
    }
  };
  doHandleUrlChange = async (filePath, viewModeOverride, primerPathOverride, locationOverride) => {
    window.isReloadingFile = true;
    try {
      const oldReadOnly = this.state.isReadOnly;
      const viewMode = viewModeOverride || this.state.viewMode;
      const primerPath = primerPathOverride !== void 0 ? primerPathOverride : this.state.currentPrimerPath;
      const locationParam = locationOverride !== void 0 ? locationOverride : this.state.currentLocation;
      const suppressReload = this.shouldSuppressReload();
      const isReMountingSameFile = this.state.currentFilePath ? this.state.currentFilePath === filePath : false;
      const suppressSpinner = suppressReload && isReMountingSameFile;
      if (filePath && viewMode !== "table") {
        sessionStorage.setItem("ove_last_loaded_file", filePath);
        this.addToHistory(filePath);
      }
      const urlParams = window.location.hash.includes("?") ? new URLSearchParams(window.location.hash.split("?")[1]) : new URLSearchParams();
      const isNew = urlParams.get("new") === "true";
      const newType = urlParams.get("type");
      const isReadOnlyParam = urlParams.get("readOnly") === "true";
      this.setState({
        currentFilePath: filePath,
        currentPrimerPath: primerPath,
        currentLocation: locationParam,
        isLoading: !suppressSpinner && !isNew,
        lastServerTimestamp: null,
        newServerTimestamp: null,
        currentDescription: null,
        isNewFile: isNew,
        isReadOnly: isReadOnlyParam
      });
      const filename = filePath ? filePath.split("/").pop() : "";
      if (filePath) {
        this.updateFilenameDisplay(filename);
        document.title = `${filename} | OVE`;
      } else {
        this.updateFilenameDisplay("OVE");
        document.title = "Recent Files | OVE";
      }
      const isAssembly = filename.toLowerCase().endsWith(".assembly");
      const isJBrowse = filename.toLowerCase().endsWith(".jbrowse.zip");
      const isSamJson = filename.toLowerCase().endsWith(".sam.json");
      const isAb1 = filename.toLowerCase().endsWith(".ab1");
      const isGenGb = filename.toLowerCase().endsWith(".gen.gb");
      const editorType = isSamJson ? "samJson" : isAssembly ? "yaml" : isJBrowse ? "jbrowse" : "vector";
      const finalReadOnly = isReadOnlyParam || isAb1 || isGenGb;
      this.setState({
        editorType,
        isReadOnly: finalReadOnly
      });
      sessionStorage.setItem("ove_last_editor_type", editorType);
      this.fetchFolders();
      if (viewMode === "bound-sequences") {
        if (this.state.currentPrimerPath !== primerPath || this.state.boundSequences.length === 0) {
          this.setState({ isLoading: true });
          try {
            const response = await axios.get(
              `/api/find-bound-sequences?path=${encodeURIComponent(primerPath)}`
            );
            this.setState({ boundSequences: response.data });
          } catch (error) {
            console.error("Error fetching bound sequences:", error);
            window.toastr?.error("Error fetching bound sequences");
          } finally {
            this.setState({ isLoading: false });
          }
        } else {
          this.setState({ isLoading: false });
        }
        if (!filePath) {
          return;
        }
      }
      if ((viewMode === "table" || viewMode === "recent") && !isFile(filePath) || !filePath && viewMode !== "bound-sequences" || filePath === "projects") {
        this.setState({
          isLoading: false,
          currentFilePath: filePath === "projects" ? null : filePath
        });
        return;
      }
      if (isNew) {
        this.setState({ isLoading: false });
        const filename2 = filePath.split("/").pop();
        if (newType === "sequence") {
          const circular = urlParams.get("circular") === "true";
          this.updateEditorWithFile({
            name: filename2.replace(/\.(gb|gbk|genbank|gen\.gb)$/i, ""),
            sequence: "",
            circular,
            features: []
          }, filePath);
        } else if (newType === "assembly") {
          const method = urlParams.get("method") || "fragment_synthesis";
          const componentsStr = urlParams.get("components");
          let components = [];
          if (componentsStr) {
            try {
              const parsed = JSON.parse(decodeURIComponent(componentsStr));
              if (Array.isArray(parsed)) {
                components = parsed;
              }
            } catch (e) {
              console.error("Error parsing components:", e);
            }
          }
          const content = getAssemblyScaffold(method, components);
          this.setState({ yamlContent: content, editorType: "yaml" });
          sessionStorage.setItem("ove_last_yaml_content", content);
          sessionStorage.setItem("ove_last_editor_type", "yaml");
        }
        return;
      }
      if (isJBrowse) {
        this.setState({ isLoading: true });
        try {
          const response = await axios.post("/api/jbrowse/extract", {
            path: filePath
          });
          if (response.data.success) {
            this.setState({ jbrowseBundleId: response.data.bundleId });
            sessionStorage.setItem("ove_last_jbrowse_bundle_id", response.data.bundleId);
          }
        } catch (error) {
          console.error("Error extracting JBrowse bundle:", error);
          window.toastr.error("Error loading JBrowse viewer");
        } finally {
          this.setState({ isLoading: false });
        }
        return;
      }
      try {
        const encodedPath = filePath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
        let fileData;
        if (isAb1) {
          const response = await axios.get(
            `/api/file/${encodedPath}?t=${Date.now()}`,
            { responseType: "blob" }
          );
          const results = await anyToJson(response.data, {
            fileName: filename,
            acceptParts: true
          });
          if (results[0].success) {
            fileData = results[0].parsedSequence;
          } else {
            console.error("Error parsing ab1:", results[0].messages);
            window.toastr.error("Error parsing ab1 file");
            this.setState({ isLoading: false });
            return;
          }
        } else {
          const response = await axios.get(
            `/api/file/${encodedPath}?t=${Date.now()}`
          );
          fileData = response.data;
        }
        if (isAssembly) {
          this.setState({ yamlContent: fileData, editorType: "yaml" });
          sessionStorage.setItem("ove_last_yaml_content", fileData);
          sessionStorage.setItem("ove_last_editor_type", "yaml");
        } else if (isSamJson) {
          const id = filePath.replace(/\//g, "_");
          if (fileData.metadata?.warnings) {
            fileData.metadata.warnings.forEach((w) => {
              window.toastr?.warning(w);
            });
          }
          addAlignment(store, {
            ...fileData,
            id,
            isInitialLoad: true,
            alignmentAnnotationVisibility: {
              features: true,
              parts: true,
              dnaColors: false,
              sequence: true,
              axis: true,
              mismatches: true
            },
            alignmentAnnotationLabelVisibility: {
              features: true,
              parts: true
            }
          });
          this.setState({ samJsonId: id, currentFilePath: filePath });
          sessionStorage.setItem("ove_last_sam_json_id", id);
        } else {
          this.updateEditorWithFile(fileData, filePath, finalReadOnly);
          if (oldReadOnly !== finalReadOnly) {
            this.mountEditor();
          }
        }
        try {
          if (fileData && fileData.last_modified !== void 0) {
            this.setState({
              lastServerTimestamp: fileData.last_modified,
              currentFileHash: fileData.file_hash,
              currentDescription: fileData.description || ""
            });
            const filename2 = filePath.split("/").pop();
            this.updateFilenameDisplay(filename2, fileData.description || "");
          } else {
            const metaResponse = await axios.get(
              `/api/file/metadata/${encodedPath}?t=${Date.now()}`
            );
            this.setState({
              lastServerTimestamp: metaResponse.data.last_modified,
              currentFileHash: metaResponse.data.file_hash,
              currentDescription: metaResponse.data.description
            });
            const filename2 = filePath.split("/").pop();
            this.updateFilenameDisplay(filename2, metaResponse.data.description);
            if (isAssembly && metaResponse.data.in_inventory !== void 0) {
              if (!this.isDirty()) {
                const currentContent = fileData || "";
                const updatedContent = updateAssemblyInventoryStatusInText(
                  currentContent,
                  metaResponse.data.in_inventory
                );
                this.setState({
                  yamlContent: updatedContent
                });
                sessionStorage.setItem("ove_last_yaml_content", updatedContent);
              }
            }
          }
        } catch (e) {
          console.error("Error fetching metadata:", e);
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          const filename2 = filePath.split("/").pop();
          const hasDirectory = filePath.includes("/");
          const isResolvable = filename2.match(/^(pDTX|sDTX)\d+/i) || filename2.match(/\.(gen\.)?gb$|\.gbk$|\.genbank$|\.assembly$/i);
          if (isResolvable && hasDirectory) {
            const resolveAttemptKey = `resolve_attempt_${filename2}`;
            const lastAttempt = sessionStorage.getItem(resolveAttemptKey);
            const now = Date.now();
            if (!lastAttempt || now - parseInt(lastAttempt) > 5e3) {
              sessionStorage.setItem(resolveAttemptKey, now.toString());
              console.warn(
                `[StandaloneDemo] File not found at ${filePath}. Attempting auto-resolve for ${filename2}...`
              );
              window.location.href = "/" + filename2;
              return;
            }
          }
          if (filePath.toLowerCase().endsWith(".gen.gb")) {
            const assemblyPath = filePath.replace(/\.gen\.gb$/i, ".assembly");
            console.warn(
              `[StandaloneDemo] .gen.gb file not found: ${filePath}. Redirecting to ${assemblyPath}`
            );
            window.location.hash = this.getHashUrl(assemblyPath);
            return;
          }
          window.toastr.error(`File ${filename2} not found in projects folder.`);
          window.location.hash = "#/Standalone";
          return;
        }
        console.error("Error loading file:", error);
        window.toastr.error("Error loading file: " + getErrorMessage(error));
      } finally {
        this.setState({ isLoading: false });
      }
    } finally {
      window.isReloadingFile = false;
    }
  };
  handleSaveAsPrimerEvent = (e) => {
    this.handleSaveAsPrimer(e.detail.dna);
  };
  handleUnsavedConfirm = async () => {
    const { pendingFilePath, pendingMoveCallback, pendingAction, currentFilePath, isNewFile } = this.state;
    if (isNewFile) {
      window.dispatchEvent(new CustomEvent("file-discarded", { detail: { path: currentFilePath } }));
      this.setState({ isNewFile: false });
    }
    this.setState({
      isUnsavedAlertOpen: false,
      pendingFilePath: null,
      pendingMoveCallback: null,
      pendingAction: null
    });
    if (pendingMoveCallback) {
      pendingMoveCallback();
    } else if (pendingFilePath) {
      await this.doHandleUrlChange(pendingFilePath);
    } else if (pendingAction === "checkLessonsLearned") {
      await this.doCheckLessonsLearned();
    } else if (pendingAction === "registerSequence") {
      this.promptRegisterSequence();
    } else if (pendingAction && pendingAction.type === "designRtQpcrPrimers") {
      await this.doDesignRtQpcrPrimers(pendingAction.featureName);
    } else if (pendingAction && pendingAction.type === "exportFasta") {
      const { currentFilePath: currentFilePath2 } = this.state;
      if (currentFilePath2) {
        await this.doHandleUrlChange(currentFilePath2);
      }
      pendingAction.resolve(true);
    }
  };
  handleUnsavedSave = async () => {
    const { pendingFilePath, pendingMoveCallback, pendingAction } = this.state;
    const success = await this.handleSave();
    if (success) {
      this.setState({
        isUnsavedAlertOpen: false,
        pendingFilePath: null,
        pendingMoveCallback: null,
        pendingAction: null
      });
      if (pendingMoveCallback) {
        pendingMoveCallback();
      } else if (pendingFilePath) {
        await this.doHandleUrlChange(pendingFilePath);
      } else if (pendingAction === "checkLessonsLearned") {
        await this.doCheckLessonsLearned();
      } else if (pendingAction === "registerSequence") {
        this.promptRegisterSequence();
      } else if (pendingAction && pendingAction.type === "designRtQpcrPrimers") {
        await this.doDesignRtQpcrPrimers(pendingAction.featureName);
      } else if (pendingAction && pendingAction.type === "exportFasta") {
        pendingAction.resolve(true);
      }
    }
  };
  handleUnsavedCancel = () => {
    const { pendingAction, currentFilePath } = this.state;
    this.setState({
      isUnsavedAlertOpen: false,
      pendingFilePath: null,
      pendingMoveCallback: null,
      pendingAction: null
    });
    if (pendingAction && pendingAction.type === "exportFasta") {
      pendingAction.resolve(false);
    }
    if (currentFilePath) {
      window.location.hash = this.getHashUrl(currentFilePath);
    }
  };
  handleBeforeExport = (format) => {
    if (format === "fasta" && this.isDirty()) {
      return new Promise((resolve) => {
        this.setState({
          isUnsavedAlertOpen: true,
          pendingAction: {
            type: "exportFasta",
            resolve
          }
        });
      });
    }
    return true;
  };
  updateEditorWithFile = (fileData, filePath, readOnlyOverride) => {
    if (fileData.chromatogramData) {
      fileData.chromatogramData = convertBasePosTraceToPerBpTrace(fileData.chromatogramData);
    }
    const isCircular = fileData?.circular ?? true;
    const isReadOnly = readOnlyOverride !== void 0 ? readOnlyOverride : this.state.isReadOnly || false;
    try {
      if (!this.editor) {
        this.pendingFileData = fileData;
        this.pendingReadOnly = isReadOnly;
        return;
      }
      let selectionLayer = { start: -1, end: -1 };
      let caretPosition = -1;
      let findToolUpdate = {};
      let targetLocation = this.state.currentLocation;
      if (!targetLocation && this.state.boundSequences?.length) {
        const boundResult = this.state.boundSequences.find((s) => s.path === filePath);
        if (boundResult) {
          targetLocation = boundResult.location;
        }
      }
      if (targetLocation) {
        const match = targetLocation.match(/\[(\d+):(\d+)\]\(([-+])\)/);
        if (match) {
          selectionLayer = {
            start: parseInt(match[1], 10),
            end: parseInt(match[2], 10) - 1,
            forward: match[3] === "+"
          };
        }
      }
      if (window.activeDNAQuery) {
        const matches = findSequenceMatches(
          fileData.sequence,
          window.activeDNAQuery,
          {
            isCircular: fileData.circular,
            isAmbiguous: true,
            searchReverseStrand: true
          }
        );
        if (matches && matches.length > 0) {
          const firstMatch = matches[0];
          selectionLayer = {
            start: firstMatch.start,
            end: firstMatch.end,
            forward: !firstMatch.bottomStrand
          };
        } else {
          caretPosition = -1;
        }
        findToolUpdate = {
          findTool: {
            isOpen: true,
            searchText: window.activeDNAQuery,
            dnaOrAA: "DNA",
            ambiguousOrLiteral: "AMBIGUOUS"
          }
        };
      }
      this.editor.updateEditor({
        readOnly: isReadOnly,
        sequenceData: fileData,
        sequenceDataHistory: { past: [], future: [] },
        selectionLayer,
        caretPosition,
        ...findToolUpdate,
        annotationVisibility: {
          cutsites: true,
          cutsitesInSequence: true,
          chromatogram: !!fileData.chromatogramData
        },
        panelsShown: [
          [
            { id: "circular", name: "Circular Map", active: false },
            { id: "rail", name: "Linear Map", active: true }
          ],
          [
            { id: "sequence", name: "Sequence Map", active: true },
            { id: "properties", name: "Properties", active: false }
          ]
        ]
      });
      const state = this.editor.getState();
      this.editor.updateEditor({
        lastSavedId: state.sequenceData?.stateTrackingId
      });
    } catch (e) {
      console.error("[OVE] Update crash caught. Queuing for retry.");
      this.pendingFileData = fileData;
      setTimeout(() => this.updateEditorWithFile(fileData, filePath), 500);
    }
  };
  handleSave = async (opts, data) => {
    const { currentFilePath, editorType, isReadOnly } = this.state;
    if (!currentFilePath) return;
    if (currentFilePath.toLowerCase().endsWith(".gen.gb")) {
      this.setState({ isGenGbSaveWarningOpen: true });
      return;
    }
    if (isReadOnly) return;
    let saveData = data;
    const filterPreview = (features) => {
      if (!features) return features;
      if (Array.isArray(features)) {
        return features.filter((f) => !f.isPreview);
      }
      return Object.keys(features).reduce((acc, key) => {
        if (!features[key].isPreview) {
          acc[key] = features[key];
        }
        return acc;
      }, {});
    };
    if (saveData && saveData.features) {
      saveData = {
        ...saveData,
        features: filterPreview(saveData.features)
      };
    }
    if (!saveData) {
      if (editorType === "vector" && this.editor) {
        let state = this.editor.getState();
        if (state && state.sequenceData && state.sequenceData.features) {
          state = {
            ...state,
            sequenceData: {
              ...state.sequenceData,
              features: filterPreview(state.sequenceData.features)
            }
          };
        }
        saveData = state;
      } else if (editorType === "yaml" && this.yamlEditor) {
        saveData = this.yamlEditor.getValue ? this.yamlEditor.getValue() : "";
      }
    }
    try {
      this.isSaving = true;
      this.setState({ isSaving: true });
      const isYaml = editorType === "yaml" || typeof saveData === "string";
      const encodedPath = currentFilePath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
      const response = await axios.post(
        `/api/file/${encodedPath}`,
        saveData,
        isYaml ? { headers: { "Content-Type": "text/plain" } } : void 0
      );
      if (response.data.last_modified) {
        const newCache = { ...this.getLessonsLearnedCache() };
        delete newCache[currentFilePath];
        this.setLessonsLearnedCache(newCache);
        this.setState({
          lastServerTimestamp: response.data.last_modified,
          currentFileHash: response.data.file_hash
        });
        if (this.state.isNewFile) {
          this.setState({ isNewFile: false });
          const newHash = `#/Standalone?file=${encodeURIComponent(currentFilePath)}`;
          window.history.replaceState(null, "", window.location.pathname + window.location.search + newHash);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("file-saved"));
          }, 100);
        }
      }
      if (response.data.build_output || response.data.alignment_output) {
        this.setState({
          buildOutput: response.data.build_output || "",
          alignmentOutput: response.data.alignment_output || "",
          isBuildOutputDialogOpen: true
        });
        if (response.data.changed_by_build) {
          await this.handleUrlChange(null, true);
        }
      }
      window.toastr.success("Saved successfully!");
      window.dispatchEvent(new CustomEvent("file-saved"));
      if (editorType === "vector" && this.editor) {
        const currentState = this.editor.getState();
        if (currentState && currentState.sequenceData) {
          this.setState({
            currentDescription: currentState.sequenceData.description
          });
          this.editor.updateEditor({
            lastSavedId: currentState.sequenceData.stateTrackingId
          });
        }
      } else if (editorType === "yaml" && this.yamlEditor) {
        const content = this.yamlEditor.getValue?.();
        if (!content) return true;
        const descMatch = content.match(/^description\s*:\s*(.*)$/m);
        if (descMatch) {
          let desc = descMatch[1].trim();
          desc = desc.replace(/^["'](.*)["']$/, "$1");
          this.setState({ currentDescription: desc });
        }
        this.yamlEditor.markClean?.();
      }
      return true;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      if (error.response && error.response.status === 400) {
        console.error(
          `[OVE] Save failed with 400 Bad Request: ${errorMessage}. Stack trace for debugging:`,
          new Error().stack
        );
      }
      window.toastr.error("Save failed: " + errorMessage);
      throw error;
    } finally {
      this.isSaving = false;
      this.setState({ isSaving: false });
    }
  };
  handleAlignmentSave = async (tracksOrPairwiseTracks) => {
    const { currentFilePath, isReadOnly } = this.state;
    if (!currentFilePath || isReadOnly) return;
    const samData = {};
    const alignmentId = this.state.samJsonId || currentFilePath.replace(/\//g, "_");
    const state = store.getState();
    const alignment = state?.VectorEditor?.__allEditorsOptions?.alignments?.[alignmentId];
    if (!tracksOrPairwiseTracks) {
      if (!alignment) {
        window.toastr.error("Alignment save failed: Could not find alignment tracks to save.");
        return false;
      }
      const cleanTrack = (t) => {
        const clone = { ...t };
        delete clone.matchHighlightRanges;
        delete clone.additionalSelectionLayers;
        delete clone.mismatches;
        return clone;
      };
      if (alignment.id) samData.id = alignment.id;
      if (alignment.alignmentType) samData.alignmentType = alignment.alignmentType;
      if (alignment.pairwiseAlignments) {
        samData.pairwiseAlignments = alignment.pairwiseAlignments.map((pair) => pair.map(cleanTrack));
      } else if (alignment.alignmentTracks) {
        samData.alignmentTracks = alignment.alignmentTracks.map(cleanTrack);
      }
    } else {
      if (alignment && alignment.id) samData.id = alignment.id;
      if (alignment && alignment.alignmentType) samData.alignmentType = alignment.alignmentType;
      if (tracksOrPairwiseTracks[0] && Array.isArray(tracksOrPairwiseTracks[0])) {
        samData.pairwiseAlignments = tracksOrPairwiseTracks;
      } else {
        samData.alignmentTracks = tracksOrPairwiseTracks;
      }
    }
    try {
      this.isSaving = true;
      this.setState({ isSaving: true });
      const encodedPath = currentFilePath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
      await axios.post(`/api/file/${encodedPath}`, samData);
      const metaResponse = await axios.get(
        `/api/file/metadata/${encodedPath}?t=${Date.now()}`
      );
      this.setState({
        lastServerTimestamp: metaResponse.data.last_modified
      });
      window.toastr.success("Alignment saved successfully!");
      return true;
    } catch (error) {
      window.toastr.error("Alignment save failed: " + getErrorMessage(error));
      return false;
    } finally {
      this.isSaving = false;
      this.setState({ isSaving: false });
    }
  };
  handleCleanupFailedAlignments = async () => {
    try {
      const response = await axios.post("/api/alignments/cleanup-failed");
      const { deleted_count } = response.data;
      if (deleted_count > 0) {
        window.toastr.success(`Deleted ${deleted_count} failed alignment files.`);
        window.dispatchEvent(new CustomEvent("refresh-file-explorer"));
      } else {
        window.toastr.info("No failed alignment files found.");
      }
    } catch (error) {
      console.error("Failed to cleanup alignments:", error);
      window.toastr.error(`Failed to cleanup alignments: ${getErrorMessage(error)}`);
    }
  };
  menuFilter = (menuDef) => {
    const filteredMenu = menuDef.map((item) => {
      if (item && item.text === "File") {
        const fileSubmenu = [...item.submenu];
        const existingSaveAsIndex = fileSubmenu.findIndex(
          (sub) => typeof sub === "string" && sub === "saveSequenceAs" || sub && sub.cmd === "saveSequenceAs" || sub && sub.text === "Save As..."
        );
        return {
          ...item,
          submenu: fileSubmenu
        };
      }
      return item;
    });
    if (this.state.currentFilePath?.endsWith(".gen.gb")) {
      const currentFile = this.state.availableFiles?.find(
        (f) => f.path === this.state.currentFilePath
      );
      const realPath = currentFile?.isSymlink && currentFile?.symlinkTarget || this.state.currentFilePath;
      const assemblyPath = realPath.replace(/\.gen\.gb$/, ".assembly");
      filteredMenu.push({
        text: "View Assembly",
        title: "View the original assembly configuration",
        onClick: () => {
          window.location.hash = this.getHashUrl(assemblyPath);
        },
        icon: "settings"
      });
    }
    if (this.state.currentFilePath?.match(/\.(gen\.)?gb$/)) {
      filteredMenu.push({
        text: "Check Lessons Learned",
        title: "Run the lessons learned checker on this file",
        onClick: this.handleCheckLessonsLearned,
        icon: "info-sign",
        intent: Intent.PRIMARY,
        minimal: true
      });
    }
    const registryHost = this.state.registryHost || (window.location.hostname === "main.delvetx.com" ? "https://main.delvetx.com/registry" : "http://localhost/registry");
    const rrtHost = registryHost.replace("/registry", "/rrt/");
    filteredMenu.push({
      text: "More Apps",
      submenu: [
        {
          text: "Registry",
          icon: "database",
          onClick: () => {
            window.open(registryHost, "_blank");
          }
        },
        {
          text: "Risk Reduction Tree",
          icon: "diagram-tree",
          onClick: () => {
            window.open(rrtHost, "_blank");
          }
        }
      ]
    });
    return filteredMenu;
  };
  renderMoreAppsDropdown = () => {
    const registryHost = this.state.registryHost || (window.location.hostname === "main.delvetx.com" ? "https://main.delvetx.com/registry" : "http://localhost/registry");
    const rrtHost = registryHost.replace("/registry", "/rrt/");
    return /* @__PURE__ */ jsx(
      Popover,
      {
        position: Position.BOTTOM_LEFT,
        content: /* @__PURE__ */ jsxs(Menu, { children: [
          /* @__PURE__ */ jsx(
            MenuItem,
            {
              text: "Registry",
              icon: "database",
              onClick: () => window.open(registryHost, "_blank")
            }
          ),
          /* @__PURE__ */ jsx(
            MenuItem,
            {
              text: "Risk Reduction Tree",
              icon: "diagram-tree",
              onClick: () => window.open(rrtHost, "_blank")
            }
          )
        ] }),
        children: /* @__PURE__ */ jsx(
          Button,
          {
            minimal: true,
            small: true,
            icon: "applications",
            rightIcon: "caret-down",
            text: "More Apps",
            style: { marginLeft: 4, marginRight: 4 }
          }
        )
      }
    );
  };
  handleSaveAsOve = (event, sequenceData) => {
    this.handleSaveAs();
  };
  handleSaveAs = () => {
    const { currentFilePath } = this.state;
    if (!currentFilePath) return;
    const parts = currentFilePath.split("/");
    const filename = parts.pop();
    const folder = parts.join("/");
    this.setState({
      isSaveAsDialogOpen: true,
      saveAsFilename: filename,
      saveAsFolder: folder || ""
    });
    this.fetchFolders();
  };
  handleExtractSelection = (start, end, defaultName) => {
    let defFolder = ".";
    let extractFilename = defaultName || "extracted_sequence";
    const filePath = this.state.currentFilePath;
    if (filePath) {
      const parts = filePath.split("/");
      const currentBasename = parts.pop();
      defFolder = parts.join("/") || ".";
      if (!defaultName || defaultName === "extracted_region") {
        extractFilename = `from_${currentBasename}`;
        if (!extractFilename.toLowerCase().endsWith(".gb")) {
          extractFilename += ".gb";
        }
      }
    }
    this.setState({
      isExtractDialogOpen: true,
      extractFilename,
      extractStart: start,
      extractEnd: end,
      extractFolder: defFolder
    });
    this.fetchFolders();
  };
  handleSaveAsPrimer = (sequence, suggestedName) => {
    this.setState({
      isSaveAsPrimerDialogOpen: true,
      saveAsPrimerSequence: sequence,
      saveAsPrimerFilename: suggestedName ? suggestedName + ".gb" : "primer.gb",
      saveAsPrimerFolder: this.state.lastUsedPrimerFolder
    });
    this.fetchFolders();
  };
  onSaveAsPrimerConfirm = async () => {
    const {
      saveAsPrimerFilename,
      saveAsPrimerFolder,
      saveAsPrimerSequence,
      currentFilePath,
      yamlContent
    } = this.state;
    if (!saveAsPrimerFilename) {
      window.toastr.error("Filename is required");
      return;
    }
    if (!saveAsPrimerSequence) {
      window.toastr.error("Primer sequence is missing");
      return;
    }
    const trimmedFilename = saveAsPrimerFilename.trim().replace(/[^a-zA-Z0-9\-\.]+/g, "_").replace(/^_+|_+$/g, "");
    const cleanFilename = trimmedFilename.toLowerCase().endsWith(".gb") ? trimmedFilename : trimmedFilename + ".gb";
    const newPath = saveAsPrimerFolder && saveAsPrimerFolder !== "." ? `${saveAsPrimerFolder}/${cleanFilename}` : cleanFilename;
    this.setState({ isSavingAsPrimer: true });
    try {
      const encodedPath = newPath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
      const primerName = cleanFilename.replace(/\.gb$/i, "");
      await axios.post(`/api/file/${encodedPath}`, {
        sequence: saveAsPrimerSequence,
        name: primerName,
        features: [
          {
            name: primerName,
            type: "primer_bind",
            start: 0,
            end: saveAsPrimerSequence.length - 1,
            forward: true
          }
        ]
      });
      if (currentFilePath && currentFilePath.toLowerCase().endsWith(".assembly")) {
        const lines = yamlContent.split("\n");
        const updatedLines = lines.map((line) => {
          const match = line.match(
            /^(\s*)([a-zA-Z0-9_-]+):\s*([A-Za-z0-9_-]+)\s*$/
          );
          if (match) {
            const indent = match[1];
            const key = match[2];
            const val = match[3];
            if (val === saveAsPrimerSequence) {
              if (key === primerName) {
                let newKey = key;
                const lowerKey = key.toLowerCase();
                if (lowerKey.includes("fwd")) newKey = "fwd";
                else if (lowerKey.includes("rvs") || lowerKey.includes("rev"))
                  newKey = "rvs";
                return `${indent}${newKey}: ${cleanFilename}`;
              }
              return `${indent}${key}: ${cleanFilename}`;
            }
          }
          return line.split(saveAsPrimerSequence).join(cleanFilename);
        });
        const updatedContent = updatedLines.join("\n");
        const encodedAssemblyPath = currentFilePath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
        const resp = await axios.post(`/api/file/${encodedAssemblyPath}`, updatedContent, {
          headers: { "Content-Type": "text/plain" }
        });
        window.toastr.success("Primer saved and assembly updated!");
        this.setState({
          isSaveAsPrimerDialogOpen: false,
          lastUsedPrimerFolder: saveAsPrimerFolder,
          yamlContent: updatedContent,
          lastServerTimestamp: resp.data.last_modified
        });
      } else {
        window.toastr.success("Primer saved successfully!");
        this.setState({
          isSaveAsPrimerDialogOpen: false,
          lastUsedPrimerFolder: saveAsPrimerFolder
        });
      }
      this.fetchFolders();
    } catch (error) {
      console.error("Save as primer failed:", error);
      window.toastr.error("Save as primer failed: " + getErrorMessage(error));
    } finally {
      this.setState({ isSavingAsPrimer: false });
    }
  };
  onExtractConfirm = async () => {
    const { extractFilename, extractStart, extractEnd, extractFolder } = this.state;
    if (!extractFilename) {
      window.toastr.error("Filename is required");
      return;
    }
    const trimmedFilename = extractFilename.trim().replace(/[^a-zA-Z0-9\-\.]+/g, "_").replace(/^_+|_+$/g, "");
    const cleanFilename = SEQUENCE_EXTENSIONS.some(
      (ex) => trimmedFilename.toLowerCase().endsWith(ex)
    ) ? trimmedFilename : trimmedFilename + ".gb";
    const newPath = extractFolder ? `${extractFolder}/${cleanFilename}` : cleanFilename;
    this.setState({ isExtracting: true });
    try {
      if (!this.editor) return;
      const { sequenceData } = this.editor.getState();
      const extractedData = getSequenceDataBetweenRange(sequenceData, {
        start: extractStart,
        end: extractEnd
      });
      const sourcePath = this.state.currentFilePath || "unknown";
      const sourceFilename = sourcePath.split("/").pop();
      if (extractedData.features) {
        if (Array.isArray(extractedData.features)) {
          extractedData.features.forEach((f) => {
            f.transferredFrom = sourceFilename;
          });
        } else {
          Object.values(extractedData.features).forEach((f) => {
            f.transferredFrom = sourceFilename;
          });
        }
      }
      const encodedPath = newPath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
      await axios.post(`/api/file/${encodedPath}`, extractedData);
      window.toastr.success("Extracted to: " + cleanFilename);
      this.setState({
        isExtractDialogOpen: false,
        lessonsLearnedCache: {
          ...this.state.lessonsLearnedCache,
          [newPath]: null
        }
      });
      window.location.hash = this.getHashUrl(newPath);
    } catch (error) {
      window.toastr.error("Extraction failed: " + getErrorMessage(error));
    } finally {
      this.setState({ isExtracting: false });
    }
  };
  handleExtractOpened = () => {
    if (this.extractInputRef.current) {
      this.extractInputRef.current.focus();
      this.extractInputRef.current.setSelectionRange(0, 0);
    }
  };
  handleExtractKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      this.onExtractConfirm();
    }
  };
  renderRunBuildButton = () => {
    return /* @__PURE__ */ jsx(
      Button,
      {
        minimal: true,
        small: true,
        icon: "wrench",
        title: "Run Build",
        "data-testid": "run-build-button",
        onClick: () => this.handleRunBuild()
      }
    );
  };
  handleRegisterSequence = () => {
    const { currentFilePath } = this.state;
    if (!currentFilePath) return;
    if (this.isDirty()) {
      this.setState({
        isUnsavedAlertOpen: true,
        pendingAction: "registerSequence"
      });
      return;
    }
    this.promptRegisterSequence();
  };
  promptRegisterSequence = () => {
    this.setState({ isRegisterConfirmOpen: true });
  };
  doRegisterSequence = async (forceNewId = false) => {
    const {
      currentFilePath,
      currentDescription,
      availableFiles,
      isRegistering,
      editorType,
      yamlContent
    } = this.state;
    if (!currentFilePath || isRegistering && !forceNewId) return;
    let finalDescription = currentDescription;
    if (editorType === "vector" && this.editor) {
      const editorState = this.editor.getState();
      finalDescription = editorState?.sequenceData?.description || finalDescription;
    } else if (editorType === "yaml") {
      const descMatch = yamlContent.match(/^description\s*:\s*(.*)$/m);
      if (descMatch) {
        let desc = descMatch[1].trim();
        desc = desc.replace(/^["'](.*)["']$/, "$1");
        finalDescription = desc;
      }
    }
    this.setState({
      isRegisterConfirmOpen: false,
      isIdTakenAlertOpen: false,
      isRegistering: true
    });
    try {
      const filename = currentFilePath.split("/").pop();
      const allExtensions = [...SEQUENCE_EXTENSIONS, ".assembly", ".sam.json"];
      const ext = allExtensions.sort((a, b) => b.length - a.length).find((ex) => filename.toLowerCase().endsWith(ex)) || "";
      const baseFilename = ext ? filename.slice(0, -ext.length) : filename;
      const fullMatch = baseFilename.match(/^(?:[ps]DTX)?(\d+)([a-z]+)?$/i);
      let regResponse = null;
      if (fullMatch && !forceNewId) {
        const id = fullMatch[1];
        const versionSuffix = fullMatch[2];
        if (versionSuffix && versionSuffix !== "a") {
          try {
            regResponse = await axios.post(
              `${this.state.registryHost}/api/entry/${id}/version`,
              {
                version: versionSuffix,
                description: finalDescription || ""
              },
              {
                headers: { "X-Password": "plasmid" }
              }
            );
          } catch (e) {
            console.warn(
              `Failed to register version ${versionSuffix} for ID ${id}, attempting to create base entry with ID ${id} first:`,
              e
            );
            try {
              await axios.post(
                `${this.state.registryHost}/api/entry`,
                {
                  id: parseInt(id, 10),
                  description: finalDescription || ""
                },
                {
                  headers: { "X-Password": "plasmid" }
                }
              );
              regResponse = await axios.post(
                `${this.state.registryHost}/api/entry/${id}/version`,
                {
                  version: versionSuffix,
                  description: finalDescription || ""
                },
                {
                  headers: { "X-Password": "plasmid" }
                }
              );
            } catch (e2) {
              console.warn(
                `Failed to register base entry and version for ID ${id}:`,
                e2
              );
              if (e2.response?.status === 409) {
                this.setState({
                  isIdTakenAlertOpen: true,
                  attemptedId: id + (versionSuffix || ""),
                  isRegistering: false
                });
                return;
              }
            }
          }
        } else {
          try {
            regResponse = await axios.post(
              `${this.state.registryHost}/api/entry`,
              {
                id: parseInt(id, 10),
                description: finalDescription || ""
              },
              {
                headers: { "X-Password": "plasmid" }
              }
            );
          } catch (e) {
            if (e.response?.status === 409) {
              this.setState({
                isIdTakenAlertOpen: true,
                attemptedId: id + (versionSuffix || ""),
                isRegistering: false
              });
              return;
            }
            console.warn(
              `Failed to register with ID ${id}, falling back to new ID:`,
              e
            );
          }
        }
      }
      if (!regResponse) {
        regResponse = await axios.post(`${this.state.registryHost}/api/entry`, {
          description: finalDescription || ""
        }, {
          headers: { "X-Password": "plasmid" }
        });
      }
      const entryId = regResponse.data.id;
      const entryVersion = regResponse.data.version || "a";
      let isCircular = false;
      if (this.state.editorType === "vector" && this.editor) {
        const editorState = this.editor.getState();
        isCircular = !!editorState?.sequenceData?.circular;
      } else if (this.state.editorType === "yaml") {
        isCircular = /circularize\s*:\s*true/i.test(this.state.yamlContent) || /method\s*:\s*(gibson|golden_gate|overhang|user|gateway|genscript_seamless)/i.test(this.state.yamlContent);
      }
      const prefix = isCircular ? "pDTX" : "sDTX";
      const versionPart = entryVersion === "a" ? "" : entryVersion;
      const newBaseName = `${prefix}${entryId}${versionPart}`;
      const currentFile = availableFiles?.find((f) => f.path === currentFilePath);
      const isSymlink = currentFile?.isSymlink;
      const realFilePath = isSymlink && currentFile?.symlinkTarget || currentFilePath;
      const fullPathParts = realFilePath.split("/");
      const oldFilename = fullPathParts.pop();
      const parentPath = fullPathParts.join("/");
      const isGenGb = ext === ".gen.gb";
      const newFilename = `${newBaseName}${ext}`;
      const newRealFilePath = parentPath ? `${parentPath}/${newFilename}` : newFilename;
      if (realFilePath !== newRealFilePath) {
        await axios.post("/api/file/move", {
          old_path: realFilePath,
          new_path: newRealFilePath
        });
      }
      let finalPath = newRealFilePath;
      if (isSymlink) {
        const symlinkParts = currentFilePath.split("/");
        symlinkParts.pop();
        const symlinkParent = symlinkParts.join("/");
        const newSymlinkPath = symlinkParent ? `${symlinkParent}/${newFilename}` : newFilename;
        if (currentFilePath !== newSymlinkPath) {
          await axios.post("/api/file/move", {
            old_path: currentFilePath,
            new_path: newSymlinkPath
          });
        }
        const oldAssemblySymlink = currentFilePath.replace(/\.(gen\.)?gb$/, ".assembly");
        if (oldAssemblySymlink !== currentFilePath) {
          const newAssemblySymlink = newSymlinkPath.replace(/\.(gen\.)?gb$/, ".assembly");
          if (oldAssemblySymlink !== newAssemblySymlink) {
            try {
              await axios.post("/api/file/move", {
                old_path: oldAssemblySymlink,
                new_path: newAssemblySymlink
              });
            } catch (e) {
            }
          }
        }
        finalPath = newSymlinkPath;
      }
      await this.fetchFolders();
      window.toastr.success(`Registered as ${newBaseName}`);
      window.location.hash = this.getHashUrl(finalPath);
    } catch (error) {
      console.error("Registration failed:", error);
      window.toastr.error("Registration failed: " + getErrorMessage(error));
    } finally {
      this.setState({ isRegistering: false });
    }
  };
  handleMuteCode = async (code, filename) => {
    try {
      this.setState({ isRegistering: true });
      const response = await axios.post("/api/assembly/mute", {
        code,
        filename
      });
      if (response.data.success) {
        window.toastr?.success(`Muted ${code} in ${filename}`);
        const buildOutput = response.data.build_output;
        const alignmentOutput = response.data.alignment_output;
        this.setState({ buildOutput, alignmentOutput });
        if (!buildOutput && !alignmentOutput) {
          console.log("[DEBUG] No warnings left, closing dialog");
          this.closeBuildOutputDialog();
        }
        if (response.data.path === this.state.currentFilePath) {
          this.handleUrlChange(null, true);
        }
      }
    } catch (error) {
      console.error("Mute failed:", error);
      window.toastr?.error(
        "Mute failed: " + (error.response?.data?.error || error.message)
      );
    } finally {
      this.setState({ isRegistering: false });
    }
  };
  handleRunBuild = async () => {
    this.setState({ isRegistering: true });
    window.dispatchEvent(new CustomEvent("build-started"));
    try {
      const response = await axios.post("/api/assembly/build");
      if (response.data.success) {
        const buildOutput = response.data.build_output;
        const alignmentOutput = response.data.alignment_output;
        if (!buildOutput && !alignmentOutput) {
          window.toastr.success("Everything is up to date.");
          this.closeBuildOutputDialog();
        } else {
          this.setState({
            buildOutput: buildOutput || "",
            alignmentOutput: alignmentOutput || "",
            isBuildOutputDialogOpen: true
          });
        }
        if (this.state.currentFilePath) {
          this.handleUrlChange(null, true);
        }
      }
    } catch (e) {
      window.toastr.error("Build failed: " + getErrorMessage(e));
    } finally {
      this.setState({ isRegistering: false });
      window.dispatchEvent(new CustomEvent("build-finished"));
    }
  };
  resolveFilePath = (filename) => {
    const { availableFiles } = this.state;
    if (!availableFiles) return null;
    const basename = filename.split(/[\\/]/).pop();
    const searchNames = [
      basename,
      basename + ".assembly",
      basename + ".gen.gb"
    ];
    const extraVariants = [];
    searchNames.forEach((name) => {
      if (name.toLowerCase().startsWith("pdtx")) {
        extraVariants.push("s" + name.substring(1));
      } else if (name.toLowerCase().startsWith("sdtx")) {
        extraVariants.push("p" + name.substring(1));
      } else if (name.toLowerCase().startsWith("dtx")) {
        extraVariants.push("s" + name);
        extraVariants.push("p" + name);
      }
    });
    const exactMatch = availableFiles.find(
      (f) => searchNames.some((name) => f.name.toLowerCase() === name.toLowerCase())
    );
    if (exactMatch) return exactMatch.path;
    const variantMatch = availableFiles.find(
      (f) => extraVariants.some((name) => f.name.toLowerCase() === name.toLowerCase())
    );
    return variantMatch ? variantMatch.path : null;
  };
  handleOpenFile = (filename, target = "_self") => {
    const path = this.resolveFilePath(filename);
    if (!path) {
      window.toastr?.error(`Could not resolve path for ${filename}`);
      return;
    }
    const hashUrl = this.getHashUrl(path);
    const fullUrl = window.location.origin + window.location.pathname + hashUrl;
    if (target === "_blank") {
      window.open(fullUrl, "_blank");
    } else if (target === "_window") {
      window.open(
        fullUrl,
        "_blank",
        "toolbar=yes,scrollbars=yes,resizable=yes,top=100,left=100,width=1200,height=800"
      );
    } else {
      window.location.hash = hashUrl;
      this.closeBuildOutputDialog();
    }
  };
  getLessonsLearnedCache = () => {
    try {
      const cached = sessionStorage.getItem("lessonsLearnedCache");
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      console.error("Failed to parse lessonsLearnedCache", e);
      return {};
    }
  };
  setLessonsLearnedCache = (cache) => {
    try {
      sessionStorage.setItem("lessonsLearnedCache", JSON.stringify(cache));
    } catch (e) {
      console.error("Failed to save lessonsLearnedCache", e);
    }
  };
  handleCheckLessonsLearned = async () => {
    const { currentFilePath } = this.state;
    if (!currentFilePath) return;
    if (this.isDirty()) {
      this.setState({
        isUnsavedAlertOpen: true,
        pendingAction: "checkLessonsLearned"
      });
      return;
    }
    await this.doCheckLessonsLearned();
  };
  handleGenerateVerificationPrimers = async () => {
    const { currentFilePath } = this.state;
    if (!currentFilePath) return;
    if (this.isDirty()) {
      this.setState({
        isUnsavedAlertOpen: true,
        pendingAction: "generateVerificationPrimers"
      });
      return;
    }
    await this.doGenerateVerificationPrimers();
  };
  doGenerateVerificationPrimers = async () => {
    const { currentFilePath } = this.state;
    if (!currentFilePath) return;
    this.setState({
      isVerificationPrimersDialogOpen: true,
      isGeneratingVerificationPrimers: true,
      verificationPrimersOutput: "Generating verification primers...\n"
    });
    try {
      const encodedPath = currentFilePath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
      const response = await axios.get(
        `/api/verify_primers/${encodedPath}`
      );
      this.setState({
        verificationPrimersOutput: response.data,
        isGeneratingVerificationPrimers: false
      });
    } catch (error) {
      console.error("Error generating verification primers:", error);
      this.setState({
        verificationPrimersOutput: `Error generating primers: ${error.response?.data?.error || error.message}`,
        isGeneratingVerificationPrimers: false
      });
    }
  };
  handleDesignRtQpcrPrimers = async (featureName) => {
    const { currentFilePath } = this.state;
    if (!currentFilePath) return;
    if (this.isDirty()) {
      this.setState({
        isUnsavedAlertOpen: true,
        pendingAction: { type: "designRtQpcrPrimers", featureName }
      });
      return;
    }
    await this.doDesignRtQpcrPrimers(featureName);
  };
  doDesignRtQpcrPrimers = async (featureName) => {
    const { currentFilePath } = this.state;
    if (!currentFilePath) return;
    this.setState({
      isRtQpcrPrimersDialogOpen: true,
      isGeneratingRtQpcrPrimers: true,
      rtQpcrPrimersOutput: "Designing RT-qPCR primers...\n"
    });
    try {
      const encodedPath = currentFilePath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
      const response = await axios.get(
        `/api/rt_qpcr_primers/${encodedPath}?feature=${encodeURIComponent(featureName)}`
      );
      this.setState({
        rtQpcrPrimersOutput: response.data,
        isGeneratingRtQpcrPrimers: false
      });
    } catch (error) {
      console.error("Error designing RT-qPCR primers:", error);
      this.setState({
        rtQpcrPrimersOutput: `Error designing primers: ${error.response?.data?.error || error.message}`,
        isGeneratingRtQpcrPrimers: false
      });
    }
  };
  closeRtQpcrPrimersDialog = () => {
    this.setState({ isRtQpcrPrimersDialogOpen: false });
  };
  handleRtQpcrPrimersDialogOpened = () => {
    if (this.rtQpcrPrimersDialogRef.current) {
      const dialog = this.rtQpcrPrimersDialogRef.current;
      const resizeHandle = document.createElement("div");
      resizeHandle.className = "dialog-resize-handle";
      resizeHandle.style.cssText = "position: absolute; right: 0; bottom: 0; width: 20px; height: 20px; cursor: nwse-resize; z-index: 10;";
      const content = dialog.querySelector(".bp3-dialog-body");
      if (content) {
        content.appendChild(resizeHandle);
        let startX, startY, startWidth, startHeight;
        const onMouseMove = (e) => {
          const newWidth = startWidth + (e.clientX - startX);
          const newHeight = startHeight + (e.clientY - startY);
          this.setState({
            rtQpcrPrimersDialogSize: {
              width: `${Math.max(400, newWidth)}px`,
              height: `${Math.max(300, newHeight)}px`
            }
          });
        };
        const onMouseUp = () => {
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
          localStorage.setItem("rtQpcrPrimersDialogSize", JSON.stringify(this.state.rtQpcrPrimersDialogSize));
        };
        resizeHandle.onmousedown = (e) => {
          e.preventDefault();
          startX = e.clientX;
          startY = e.clientY;
          const dialogRect = dialog.getBoundingClientRect();
          startWidth = dialogRect.width;
          startHeight = dialogRect.height;
          document.addEventListener("mousemove", onMouseMove);
          document.addEventListener("mouseup", onMouseUp);
        };
      }
    }
  };
  doCheckLessonsLearned = async () => {
    const { currentFilePath, currentFileHash, currentCheckerHash } = this.state;
    if (!currentFilePath) return;
    const cache = this.getLessonsLearnedCache();
    const entry = cache[currentFilePath];
    const isCachedObject = entry && typeof entry === "object" && entry.output;
    const cachedOutput = isCachedObject ? entry.output : entry;
    const cachedCheckerHash = isCachedObject ? entry.checkerHash : null;
    const cachedFileHash = isCachedObject ? entry.fileHash : null;
    if (cachedOutput && currentCheckerHash && cachedCheckerHash === currentCheckerHash && cachedFileHash === currentFileHash) {
      console.log(
        "[DEBUG] handleCheckLessonsLearned - Using cached result for:",
        currentFilePath,
        "with file hash:",
        currentFileHash
      );
      this.setState({
        isLessonsLearnedDialogOpen: true,
        lessonsLearnedOutput: cachedOutput,
        isCheckingLessonsLearned: false
      });
      return;
    }
    this.setState({
      isLessonsLearnedDialogOpen: true,
      isCheckingLessonsLearned: true,
      lessonsLearnedOutput: "Checking lessons learned...\n"
    });
    try {
      const encodedPath = currentFilePath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
      const response = await axios.get(
        `/api/lessons_learned/check/${encodedPath}`
      );
      const { stdout, stderr, success } = response.data;
      let output = "";
      if (stdout) output += stdout;
      if (stderr) output += `
ERROR:
${stderr}`;
      if (!stdout && !stderr) {
        output = success ? "Check complete. No issues found." : "Check failed with no output.";
      }
      if (!output.includes("Traceback")) {
        const newCache = {
          ...this.getLessonsLearnedCache(),
          [currentFilePath]: {
            output,
            checkerHash: response.data.checker_hash || this.state.currentCheckerHash,
            fileHash: currentFileHash
          }
        };
        this.setLessonsLearnedCache(newCache);
      } else {
        console.warn(
          "[WARN] lessonsLearnedOutput contains Traceback, not caching."
        );
      }
      this.setState({
        lessonsLearnedOutput: output,
        isCheckingLessonsLearned: false
      });
    } catch (error) {
      console.error("Error checking lessons learned:", error);
      this.setState({
        lessonsLearnedOutput: `Error running checker: ${error.response?.data?.error || error.message}`,
        isCheckingLessonsLearned: false
      });
    }
  };
  fetchFolders = async (force = false) => {
    if (!force && this.state.availableFiles && this.state.availableFiles.length > 0) {
      return;
    }
    try {
      let response;
      try {
        const dbRes = await axios.get(`/api/db/tree?t=${Date.now()}`);
        if (dbRes && dbRes.data && (dbRes.data.files || dbRes.data.tree)) {
          response = dbRes;
        }
      } catch (dbErr) {
      }
      if (!response) {
        response = await axios.get(`/api/files?t=${Date.now()}`);
      }
      this.processFileTree(response.data);
    } catch (error) {
      console.error("Error fetching folders and files:", error);
    }
  };
  onSaveAsConfirm = async () => {
    const { saveAsFilename, saveAsFolder } = this.state;
    if (!saveAsFilename) {
      window.toastr.error("Filename is required");
      return;
    }
    const trimmedFilename = saveAsFilename.trim().replace(/[^a-zA-Z0-9\-\.]+/g, "_").replace(/^_+|_+$/g, "");
    const cleanFilename = SEQUENCE_EXTENSIONS.some(
      (ex) => trimmedFilename.toLowerCase().endsWith(ex)
    ) ? trimmedFilename : trimmedFilename + ".gb";
    const newPath = saveAsFolder ? `${saveAsFolder}/${cleanFilename}` : cleanFilename;
    this.setState({ isSavingAs: true });
    try {
      let data;
      if (this.state.editorType === "vector" && this.editor) {
        data = this.editor.getState().sequenceData;
        if (data && data.features) {
          const filterPreview = (features) => {
            if (!features) return features;
            if (Array.isArray(features)) {
              return features.filter((f) => !f.isPreview);
            }
            return Object.keys(features).reduce((acc, key) => {
              if (!features[key].isPreview) acc[key] = features[key];
              return acc;
            }, {});
          };
          data = { ...data, features: filterPreview(data.features) };
        }
      } else if (this.state.editorType === "yaml" && this.yamlEditor) {
        data = this.yamlEditor.getValue();
      }
      if (!data) {
        window.toastr.error("No data to save");
        this.setState({ isSavingAs: false });
        return;
      }
      const encodedPath = newPath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
      await axios.post(`/api/file/${encodedPath}`, data);
      window.toastr.success("File saved as: " + cleanFilename);
      this.setState({
        isSaveAsDialogOpen: false,
        isSavingAs: false,
        lessonsLearnedCache: {
          ...this.state.lessonsLearnedCache,
          [newPath]: null
        }
      });
      window.location.hash = this.getHashUrl(newPath);
    } catch (error) {
      window.toastr.error("Save As failed: " + getErrorMessage(error));
      this.setState({ isSavingAs: false });
    }
  };
  handleYamlSave = async (content) => {
    await this.handleSave(null, content);
  };
  handleYamlChange = (content) => {
    this.setState({ yamlContent: content });
  };
  closeLessonsLearnedDialog = () => {
    if (this.lessonsLearnedDialogRef.current) {
      const dialogEl = this.lessonsLearnedDialogRef.current.closest(
        `.${Classes.DIALOG}`
      );
      if (dialogEl) {
        const { width, height } = dialogEl.getBoundingClientRect();
        localStorage.setItem(
          "lessonsLearnedDialogSize",
          JSON.stringify({ width, height })
        );
        this.setState({
          lessonsLearnedDialogSize: { width, height }
        });
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
      }
    }
    this.setState({
      isLessonsLearnedDialogOpen: false,
      lessonsLearnedOutput: ""
    });
  };
  closeVerificationPrimersDialog = () => {
    if (this.verificationPrimersDialogRef.current) {
      const dialogEl = this.verificationPrimersDialogRef.current.closest(
        `.${Classes.DIALOG}`
      );
      if (dialogEl) {
        const { width, height } = dialogEl.getBoundingClientRect();
        localStorage.setItem(
          "verificationPrimersDialogSize",
          JSON.stringify({ width, height })
        );
        this.setState({
          verificationPrimersDialogSize: { width, height }
        });
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
      }
    }
    this.setState({
      isVerificationPrimersDialogOpen: false,
      verificationPrimersOutput: ""
    });
  };
  handleDialogOpened = () => {
    this.setupDialogResize(
      this.lessonsLearnedDialogRef,
      "lessonsLearnedDialogSize"
    );
  };
  handleVerificationPrimersDialogOpened = () => {
    this.setupDialogResize(
      this.verificationPrimersDialogRef,
      "verificationPrimersDialogSize"
    );
  };
  setupDialogResize = (ref, storageKey) => {
    if (ref.current) {
      const dialogEl = ref.current.closest(`.${Classes.DIALOG}`);
      if (dialogEl) {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
        this.resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const rect = dialogEl.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              localStorage.setItem(
                storageKey,
                JSON.stringify({ width: rect.width, height: rect.height })
              );
            }
          }
        });
        this.resizeObserver.observe(dialogEl);
      }
    }
  };
  closeBuildOutputDialog = () => {
    this.setState({ isBuildOutputDialogOpen: false, buildOutput: "", alignmentOutput: "" });
  };
  handleJumpToPosition = (start, end) => {
    if (!this.editor) return;
    this.closeLessonsLearnedDialog();
    if (end !== void 0) {
      this.editor.updateEditor({
        caretPosition: start - 1,
        selectionLayer: {
          start: start - 1,
          end: end - 1
        }
      });
    } else {
      this.editor.updateEditor({
        caretPosition: start - 1
      });
    }
    setTimeout(() => {
      const editorNode = document.querySelector(".veVectorInteractionWrapper");
      if (editorNode) {
        editorNode.focus();
      }
    }, 100);
  };
  renderClickableText = (text) => {
    if (!text) return text;
    const regex = /(at\s+)(\d+)|(Window:\s+)(\d+)-(\d+)|(near\s+~)(\d+)|(at positions\s+\[)([\d, ]+)(\])|(\*\*ALERT\*\*|ALERT)|(Warning)|(error)/gi;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      if (match[1]) {
        const pos = parseInt(match[2], 10);
        parts.push(match[1]);
        parts.push(
          /* @__PURE__ */ jsx(
            "span",
            {
              role: "link",
              tabIndex: "0",
              style: {
                cursor: "pointer",
                color: "#106ba3",
                textDecoration: "underline"
              },
              onClick: () => this.handleJumpToPosition(pos),
              onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  this.handleJumpToPosition(pos);
                }
              },
              children: match[2]
            },
            match.index
          )
        );
      } else if (match[3]) {
        const start = parseInt(match[4], 10);
        const end = parseInt(match[5], 10);
        parts.push(match[3]);
        parts.push(
          /* @__PURE__ */ jsxs(
            "span",
            {
              role: "link",
              tabIndex: "0",
              style: {
                cursor: "pointer",
                color: "#106ba3",
                textDecoration: "underline"
              },
              onClick: () => this.handleJumpToPosition(start, end),
              onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  this.handleJumpToPosition(start, end);
                }
              },
              children: [
                match[4],
                "-",
                match[5]
              ]
            },
            match.index
          )
        );
      } else if (match[6]) {
        const pos = parseInt(match[7], 10);
        parts.push(match[6]);
        parts.push(
          /* @__PURE__ */ jsx(
            "span",
            {
              role: "link",
              tabIndex: "0",
              style: {
                cursor: "pointer",
                color: "#106ba3",
                textDecoration: "underline"
              },
              onClick: () => this.handleJumpToPosition(pos),
              onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  this.handleJumpToPosition(pos);
                }
              },
              children: match[7]
            },
            match.index
          )
        );
      } else if (match[8]) {
        parts.push(match[8]);
        const listStr = match[9];
        const nums = listStr.split(/(\d+)/);
        for (let i = 0; i < nums.length; i++) {
          const numPart = nums[i];
          if (/^\d+$/.test(numPart)) {
            const pos = parseInt(numPart, 10);
            parts.push(
              /* @__PURE__ */ jsx(
                "span",
                {
                  role: "link",
                  tabIndex: "0",
                  style: {
                    cursor: "pointer",
                    color: "#106ba3",
                    textDecoration: "underline"
                  },
                  onClick: () => this.handleJumpToPosition(pos),
                  onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      this.handleJumpToPosition(pos);
                    }
                  },
                  children: numPart
                },
                `${match.index}-p${i}`
              )
            );
          } else {
            parts.push(numPart);
          }
        }
        parts.push(match[10]);
      } else if (match[11]) {
        parts.push(
          /* @__PURE__ */ jsx("strong", { style: { color: "#c23030" }, children: match[11] }, match.index)
        );
      } else if (match[12]) {
        parts.push(
          /* @__PURE__ */ jsx("strong", { style: { color: "#d9822b" }, children: match[12] }, match.index)
        );
      } else if (match[13]) {
        parts.push(
          /* @__PURE__ */ jsx("strong", { style: { color: "#c23030" }, children: match[13] }, match.index)
        );
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    return parts;
  };
  renderFileLink = (filename, displayName, key, style = {}, extraMenuItems = null) => {
    const base = filename.replace(/\.(gen\.gb|assembly|gb|gbk|genbank|sam\.json)$/i, "");
    return /* @__PURE__ */ jsx(
      Popover,
      {
        position: Position.BOTTOM_LEFT,
        content: /* @__PURE__ */ jsxs(Menu, { children: [
          /* @__PURE__ */ jsxs(MenuItem, { text: "Open", icon: "document-open", children: [
            /* @__PURE__ */ jsxs(MenuItem, { text: ".gen.gb", icon: "document", children: [
              /* @__PURE__ */ jsx(
                MenuItem,
                {
                  text: "file",
                  icon: "document",
                  onClick: () => this.handleOpenFile(base + ".gen.gb")
                }
              ),
              /* @__PURE__ */ jsx(
                MenuItem,
                {
                  text: "in new tab",
                  icon: "duplicate",
                  onClick: () => this.handleOpenFile(base + ".gen.gb", "_blank")
                }
              ),
              /* @__PURE__ */ jsx(
                MenuItem,
                {
                  text: "in new window",
                  icon: "application",
                  onClick: () => this.handleOpenFile(base + ".gen.gb", "_window")
                }
              )
            ] }),
            /* @__PURE__ */ jsxs(MenuItem, { text: ".assembly", icon: "code-block", children: [
              /* @__PURE__ */ jsx(
                MenuItem,
                {
                  text: "file",
                  icon: "document",
                  onClick: () => this.handleOpenFile(base + ".assembly")
                }
              ),
              /* @__PURE__ */ jsx(
                MenuItem,
                {
                  text: "in new tab",
                  icon: "duplicate",
                  onClick: () => this.handleOpenFile(base + ".assembly", "_blank")
                }
              ),
              /* @__PURE__ */ jsx(
                MenuItem,
                {
                  text: "in new window",
                  icon: "application",
                  onClick: () => this.handleOpenFile(base + ".assembly", "_window")
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsx(MenuDivider, {}),
          /* @__PURE__ */ jsx(
            MenuItem,
            {
              text: "Copy Path",
              icon: "clipboard",
              onClick: () => {
                const path = this.resolveFilePath(filename);
                if (path) {
                  navigator.clipboard.writeText(path);
                  window.toastr?.success("Path copied to clipboard");
                }
              }
            }
          ),
          extraMenuItems && /* @__PURE__ */ jsx(MenuDivider, {}),
          extraMenuItems
        ] }),
        children: /* @__PURE__ */ jsx(
          "span",
          {
            role: "link",
            tabIndex: "0",
            style: {
              cursor: "pointer",
              color: "#106ba3",
              textDecoration: "underline",
              ...style
            },
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                this.handleOpenFile(filename);
              }
            },
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.handleOpenFile(filename);
            },
            onContextMenu: (e) => {
              e.preventDefault();
              e.currentTarget.click();
            },
            children: displayName || filename
          }
        )
      },
      key
    );
  };
  renderHighlightedOutput = (text) => {
    if (!text) return text;
    const parts = [];
    let lastIndex = 0;
    const regex = /error|\*\*ALERT\*\*|ALERT|Warning|\[([WE]\d{3})\]\s*(?:Warning|Error)?\s*\(([^)]+)\)|\[WARN\]\s*Cannot\s*recalculate\s*([\w.\/-]+\.sam\.json)|([├│└]──\s+)([\w._-]+\.(?:gen\.gb|assembly|gb|gbk|genbank)|[\w._-]+(?=\s*\())|'([\w.-]+)'|(projects\/[\w.\/-]+)|([\w.\/-]+\.(?:gen\.gb|assembly|gb|gbk|genbank))/gi;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      const matchedText = match[0];
      const code = match[1];
      const filenameInParens = match[2];
      const samJsonFile = match[3];
      const treePrefix = match[4];
      const treeName = match[5];
      const quotedName = match[6];
      const projectPath = match[7];
      const standaloneFile = match[8];
      const cleanFilename = (f) => f && f.startsWith("projects/") ? f.substring(9) : f;
      if (code && filenameInParens) {
        const extraMenuItems = /* @__PURE__ */ jsx(
          MenuItem,
          {
            text: `Mute this ${code.startsWith("W") ? "Warning" : "Error"} (${code})`,
            icon: "notifications-off",
            onClick: () => this.handleMuteCode(code, filenameInParens)
          }
        );
        parts.push(
          this.renderFileLink(
            cleanFilename(filenameInParens),
            matchedText,
            match.index,
            { fontWeight: "bold" },
            extraMenuItems
          )
        );
      } else if (samJsonFile) {
        const extraMenuItems = /* @__PURE__ */ jsx(
          MenuItem,
          {
            text: "Delete this alignment",
            icon: "trash",
            intent: Intent.DANGER,
            onClick: async () => {
              try {
                await axios.delete(
                  `/api/file/${encodeURIComponent(cleanFilename(samJsonFile))}`
                );
                window.toastr.success(`Deleted ${samJsonFile}`);
                window.dispatchEvent(new CustomEvent("refresh-file-explorer"));
              } catch (e) {
                window.toastr.error(`Failed to delete ${samJsonFile}`);
              }
            }
          }
        );
        parts.push(
          this.renderFileLink(
            cleanFilename(samJsonFile),
            matchedText,
            match.index,
            { color: "#d9822b", fontWeight: "bold" },
            extraMenuItems
          )
        );
      } else if (treePrefix && treeName) {
        parts.push(treePrefix);
        parts.push(
          this.renderFileLink(
            cleanFilename(treeName),
            treeName,
            match.index + treePrefix.length
          )
        );
      } else if (quotedName) {
        parts.push("'");
        parts.push(
          this.renderFileLink(
            cleanFilename(quotedName),
            quotedName,
            match.index + 1
          )
        );
        parts.push("'");
      } else if (projectPath) {
        parts.push(
          this.renderFileLink(
            cleanFilename(projectPath),
            projectPath,
            match.index
          )
        );
      } else if (standaloneFile) {
        parts.push(
          this.renderFileLink(
            cleanFilename(standaloneFile),
            standaloneFile,
            match.index
          )
        );
      } else {
        const isRed = /error|ALERT/i.test(matchedText);
        parts.push(
          /* @__PURE__ */ jsx(
            "strong",
            {
              style: { color: isRed ? "#c23030" : "#d9822b" },
              children: matchedText
            },
            match.index
          )
        );
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    return parts;
  };
  renderRegisterSequenceButton = () => {
    const { currentFilePath, isRegistering } = this.state;
    if (!currentFilePath) return null;
    const lowerPath = currentFilePath.toLowerCase();
    if (!lowerPath.endsWith(".gb") && !lowerPath.endsWith(".gen.gb") && !lowerPath.endsWith(".gbk") && !lowerPath.endsWith(".genbank") && !lowerPath.endsWith(".assembly")) {
      return null;
    }
    return /* @__PURE__ */ jsx(
      Button,
      {
        intent: Intent.SUCCESS,
        minimal: true,
        small: true,
        icon: "add",
        title: "Create a registry entry and rename this file",
        onClick: this.handleRegisterSequence,
        loading: isRegistering,
        style: { marginLeft: 4, marginRight: 4, whiteSpace: "nowrap" },
        children: "Register Sequence"
      }
    );
  };
  renderActiveEditor = (isInsidePreview = false) => {
    const {
      editorType,
      currentFilePath,
      yamlContent,
      availableFiles,
      samJsonId,
      jbrowseBundleId,
      isReadOnly
    } = this.state;
    if (editorType === "yaml") {
      return /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            width: "100%",
            height: "100%",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column"
          },
          children: /* @__PURE__ */ jsx(
            MonacoYamlEditor,
            {
              ref: (ref) => this.yamlEditor = ref,
              filePath: currentFilePath,
              content: yamlContent,
              availableFiles,
              onSave: this.handleYamlSave,
              onSaveAsPrimer: this.handleSaveAsPrimer,
              isInsidePreview,
              readOnly: isReadOnly,
              collabIsConnected: this.state.collabIsConnected,
              collabCollaborators: this.state.collabCollaborators || [],
              collabRoomName: this.state.currentFilePath,
              additionalToolbarButtons: /* @__PURE__ */ jsxs(React.Fragment, { children: [
                this.renderRunBuildButton(),
                this.renderRegisterSequenceButton(),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    minimal: true,
                    icon: "share",
                    title: "Copy URL",
                    onClick: () => {
                      const url = window.location.origin + window.location.pathname + window.location.hash;
                      navigator.clipboard?.writeText(url);
                      window.toastr?.success("URL copied to clipboard");
                    }
                  }
                )
              ] }),
              onNavigate: (path, target) => {
                const finalTarget = target === true ? "_window" : target === false ? null : target;
                if (finalTarget === "_blank") {
                  const url = window.location.origin + window.location.pathname + this.getHashUrl(path);
                  window.open(url, "_blank");
                } else if (finalTarget === "_window") {
                  const url = window.location.origin + window.location.pathname + this.getHashUrl(path);
                  window.open(
                    url,
                    "_blank",
                    "toolbar=yes,scrollbars=yes,resizable=yes,top=100,left=100,width=1200,height=800"
                  );
                } else {
                  window.location.hash = this.getHashUrl(path);
                }
              },
              ...currentFilePath?.endsWith(".assembly") ? (() => {
                const currentFile = availableFiles?.find(
                  (f) => f.path === currentFilePath
                );
                const realPath = currentFile?.isSymlink && currentFile?.symlinkTarget || currentFilePath;
                const productPath = realPath.replace(/\.assembly$/, ".gen.gb");
                const productExists = availableFiles?.some(
                  (f) => f.path === productPath
                );
                return {
                  onViewProduct: () => {
                    window.location.hash = this.getHashUrl(productPath);
                  },
                  onGenerateVerificationPrimers: this.handleGenerateVerificationPrimers,
                  productExists
                };
              })() : {}
            }
          )
        },
        "yaml-editor-container"
      );
    } else if (editorType === "samJson") {
      return /* @__PURE__ */ jsx(
        "div",
        {
          "data-testid": "samjson-viewer-container",
          className: "standaloneDemoNode",
          style: {
            width: "100%",
            height: "100%",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: "400px"
          },
          children: /* @__PURE__ */ jsx(
            AlignmentView,
            {
              id: samJsonId || currentFilePath?.replace(/\//g, "_"),
              hasTemplate: true,
              alignmentType: "Pairwise Alignment",
              shouldAutosave: true,
              handleAlignmentSave: this.handleAlignmentSave,
              onDeleteAllFailed: this.handleCleanupFailedAlignments
            }
          )
        },
        "samjson-viewer-container"
      );
    } else if (editorType === "jbrowse") {
      return /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            width: "100%",
            height: "100%",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            position: "relative"
          },
          children: [
            /* @__PURE__ */ jsx("div", { style: { position: "absolute", top: 10, right: 30, zIndex: 1001 }, children: /* @__PURE__ */ jsx(
              Button,
              {
                minimal: true,
                icon: "share",
                title: "Copy URL",
                onClick: () => {
                  const url = window.location.origin + window.location.pathname + window.location.hash;
                  navigator.clipboard?.writeText(url);
                  window.toastr?.success("URL copied to clipboard");
                }
              }
            ) }),
            /* @__PURE__ */ jsx(
              "iframe",
              {
                "data-testid": "jbrowse-iframe",
                title: "JBrowse Alignment Viewer",
                src: `/alignment-viewer/?bundleId=${jbrowseBundleId}`,
                style: { width: "100%", height: "100%", border: "none" }
              }
            )
          ]
        },
        "jbrowse-viewer-container"
      );
    } else {
      return /* @__PURE__ */ jsx(
        "div",
        {
          className: "standaloneDemoNode",
          "data-testid": "standalone-editor-container",
          ref: (node) => this.node = node,
          style: {
            width: "100%",
            height: "100%",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: isInsidePreview ? "0" : "200px"
          }
        },
        isInsidePreview ? "vector-editor-preview-container" : "vector-editor-container"
      );
    }
  };
  render() {
    const isFileSelected = isFile(this.state.currentFilePath);
    const isBoundSequences = this.state.viewMode === "bound-sequences";
    const showPreview = isFileSelected && (!isBoundSequences || this.state.currentFilePath !== this.state.currentPrimerPath);
    let tableFolderPath = "projects";
    if (this.state.currentFilePath) {
      if (isFileSelected) {
        const parts = this.state.currentFilePath.split("/");
        parts.pop();
        tableFolderPath = parts.join("/") || "projects";
      } else {
        tableFolderPath = this.state.currentFilePath;
      }
    }
    return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          flexGrow: 1,
          display: "flex",
          height: "100%",
          width: "100%",
          position: "relative",
          overflow: "hidden"
        },
        children: [
          this.state.showSettings && /* @__PURE__ */ jsxs(
            "div",
            {
              className: "tgOptionContainer",
              style: {
                width: 300,
                borderRight: "1px solid #ccc",
                padding: 20,
                backgroundColor: "#f5f8fa",
                flexShrink: 0
              },
              children: [
                /* @__PURE__ */ jsx("h3", { children: "Preferences" }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    fill: true,
                    intent: "primary",
                    icon: "cross",
                    onClick: () => this.setState({ showSettings: false }),
                    children: "Close"
                  }
                )
              ]
            }
          ),
          this.state.currentFilePath || this.state.viewMode === "recent" || this.state.viewMode === "table" || this.state.viewMode === "bound-sequences" ? this.state.viewMode === "table" || this.state.viewMode === "recent" || this.state.viewMode === "bound-sequences" ? /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                width: "100%",
                height: "100%",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
              },
              children: [
                this.state.viewMode === "bound-sequences" && /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      padding: "10px 20px",
                      background: "#eee",
                      borderBottom: "1px solid #ccc",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    },
                    children: [
                      /* @__PURE__ */ jsxs("strong", { children: [
                        "Bound Sequences:",
                        " ",
                        this.state.currentPrimerPath?.split("/").pop()
                      ] }),
                      /* @__PURE__ */ jsx(
                        Button,
                        {
                          icon: "arrow-left",
                          minimal: true,
                          onClick: () => {
                            window.history.back();
                          },
                          children: "Back"
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs(ReflexContainer, { orientation: "horizontal", children: [
                  /* @__PURE__ */ jsx(
                    ReflexElement,
                    {
                      minSize: 100,
                      flex: showPreview ? 1 - this.state.previewFlex : 1,
                      children: /* @__PURE__ */ jsx(
                        "div",
                        {
                          style: {
                            height: "100%",
                            overflow: "auto",
                            position: "relative"
                          },
                          children: /* @__PURE__ */ jsx(
                            ProjectFilesTableView,
                            {
                              folderPath: this.state.viewMode === "recent" ? "projects" : tableFolderPath,
                              availableFiles: this.state.availableFiles,
                              availableFolders: this.state.availableFolders,
                              customItems: this.state.viewMode === "bound-sequences" ? this.state.boundSequences : null,
                              activePreviewPath: this.state.currentFilePath,
                              onPreview: this.onPreview,
                              isRecentFiles: this.state.viewMode === "recent",
                              onNavigate: (path, view) => {
                                window.location.hash = this.getHashUrl(
                                  path,
                                  view ? { view } : {}
                                );
                              },
                              gitStatus: this.state.gitStatus
                            }
                          )
                        }
                      )
                    },
                    "table-pane"
                  ),
                  showPreview && /* @__PURE__ */ jsx(
                    ReflexSplitter,
                    {
                      style: {
                        borderTop: "2px solid #ddd",
                        zIndex: 10,
                        height: "4px",
                        cursor: "row-resize",
                        backgroundColor: "transparent"
                      }
                    }
                  ),
                  showPreview && /* @__PURE__ */ jsx(
                    ReflexElement,
                    {
                      minSize: 100,
                      flex: this.state.previewFlex,
                      onStopResize: ({ component }) => {
                        const newFlex = component.props.flex;
                        this.setState({ previewFlex: newFlex });
                        localStorage.setItem("ove_preview_flex", newFlex);
                      },
                      children: /* @__PURE__ */ jsxs(
                        "div",
                        {
                          "data-testid": "preview-pane",
                          style: {
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: "white",
                            position: "relative"
                          },
                          children: [
                            /* @__PURE__ */ jsxs(
                              "div",
                              {
                                style: {
                                  padding: "5px 10px",
                                  backgroundColor: "#f5f5f5",
                                  borderBottom: "1px solid #ddd",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                },
                                children: [
                                  /* @__PURE__ */ jsxs("span", { style: { fontWeight: "bold", fontSize: "12px" }, children: [
                                    "Preview:",
                                    " ",
                                    this.state.currentFilePath.split("/").pop()
                                  ] }),
                                  /* @__PURE__ */ jsxs("div", { children: [
                                    /* @__PURE__ */ jsx(
                                      Button,
                                      {
                                        minimal: true,
                                        icon: "maximize",
                                        small: true,
                                        title: "Expand",
                                        onClick: () => {
                                          window.location.hash = this.getHashUrl(
                                            this.state.currentFilePath,
                                            {
                                              view: "editor"
                                            }
                                          );
                                        }
                                      }
                                    ),
                                    /* @__PURE__ */ jsx(
                                      Button,
                                      {
                                        minimal: true,
                                        icon: "document-open",
                                        small: true,
                                        title: "Open in New Window",
                                        onClick: () => {
                                          window.open(
                                            `/#/?file=${encodeURIComponent(
                                              this.state.currentFilePath
                                            )}`,
                                            "_blank"
                                          );
                                        }
                                      }
                                    ),
                                    /* @__PURE__ */ jsx(
                                      Button,
                                      {
                                        minimal: true,
                                        icon: "cross",
                                        small: true,
                                        title: "Close",
                                        onClick: () => this.onPreview(this.state.currentFilePath)
                                      }
                                    )
                                  ] })
                                ]
                              }
                            ),
                            this.renderActiveEditor(true)
                          ]
                        }
                      )
                    },
                    "preview-pane"
                  )
                ] })
              ]
            },
            `${this.state.viewMode}-viewer-container`
          ) : this.state.viewMode === "graph" ? /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                width: "100%",
                height: "100%",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column"
              },
              children: [
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      padding: 10,
                      background: "#eee",
                      borderBottom: "1px solid #ccc",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    },
                    children: [
                      /* @__PURE__ */ jsxs("strong", { children: [
                        "Lineage Graph: ",
                        this.state.currentFilePath.split("/").pop()
                      ] }),
                      /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: 10 }, children: [
                        /* @__PURE__ */ jsx(
                          Button,
                          {
                            minimal: true,
                            icon: "share",
                            title: "Copy URL",
                            onClick: () => {
                              const url = window.location.origin + window.location.pathname + window.location.hash;
                              navigator.clipboard?.writeText(url);
                              window.toastr?.success("URL copied to clipboard");
                            }
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          Button,
                          {
                            icon: "document",
                            onClick: () => {
                              window.location.hash = this.getHashUrl(
                                this.state.currentFilePath
                              );
                            },
                            children: "Back to Editor"
                          }
                        )
                      ] })
                    ]
                  }
                ),
                /* @__PURE__ */ jsx(
                  GraphViewer,
                  {
                    filePath: this.state.currentFilePath,
                    onNavigate: (path) => {
                      window.location.hash = this.getHashUrl(path, {
                        view: "graph"
                      });
                    }
                  }
                )
              ]
            },
            "graph-viewer-container"
          ) : this.renderActiveEditor() : /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                width: "100%",
                height: "100%",
                backgroundColor: "#f5f8fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#5c7080",
                fontSize: 16
              },
              children: "No file selected"
            },
            "empty-state-container"
          ),
          (this.state.isLoading || this.state.isSaving) && /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1e3
              },
              children: /* @__PURE__ */ jsxs(
                "div",
                {
                  style: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center"
                  },
                  children: [
                    /* @__PURE__ */ jsx(Spinner, { size: 50, intent: Intent.PRIMARY }),
                    /* @__PURE__ */ jsx("h3", { style: { marginTop: 20 }, children: this.state.isSaving ? "Saving..." : "Loading..." })
                  ]
                }
              )
            }
          ),
          this.state.editorType === "samJson" && /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                position: "fixed",
                top: 10,
                right: 250,
                zIndex: 1001,
                display: "flex",
                gap: 10
              },
              children: /* @__PURE__ */ jsx(
                Button,
                {
                  minimal: true,
                  icon: "share",
                  title: "Copy URL",
                  onClick: () => {
                    const url = window.location.origin + window.location.pathname + window.location.hash;
                    navigator.clipboard?.writeText(url);
                    window.toastr?.success("URL copied to clipboard");
                  }
                }
              )
            }
          ),
          /* @__PURE__ */ jsxs(
            Alert,
            {
              canEscapeKeyCancel: true,
              canOutsideClickCancel: true,
              cancelButtonText: "Cancel",
              confirmButtonText: "Reload",
              icon: "warning-sign",
              intent: Intent.PRIMARY,
              isOpen: this.state.isReloadAlertOpen,
              onCancel: this.handleReloadCancel,
              onConfirm: this.handleReloadConfirm,
              children: [
                /* @__PURE__ */ jsxs("p", { children: [
                  "The file ",
                  /* @__PURE__ */ jsx("b", { children: this.state.currentFilePath }),
                  " has been modified on the server."
                ] }),
                /* @__PURE__ */ jsx("p", { children: "Do you want to reload it? Local changes will be lost." })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Alert,
            {
              canEscapeKeyCancel: true,
              canOutsideClickCancel: true,
              confirmButtonText: "OK",
              icon: "warning-sign",
              intent: Intent.WARNING,
              isOpen: this.state.isGenGbSaveWarningOpen,
              onConfirm: () => this.setState({ isGenGbSaveWarningOpen: false }),
              onClose: () => this.setState({ isGenGbSaveWarningOpen: false }),
              children: [
                /* @__PURE__ */ jsxs("p", { children: [
                  /* @__PURE__ */ jsx("b", { children: this.state.currentFilePath }),
                  " is a generated file and cannot be edited."
                ] }),
                /* @__PURE__ */ jsx("p", { children: "Edit the source assembly or sequence file instead, then rebuild to regenerate this file." })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Dialog,
            {
              isOpen: this.state.isUnsavedAlertOpen,
              onClose: this.handleUnsavedCancel,
              title: "Unsaved Changes",
              "data-testid": "unsaved-changes-dialog",
              icon: "warning-sign",
              children: [
                /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_BODY, children: [
                  /* @__PURE__ */ jsx("p", { children: "You have unsaved changes in the current file." }),
                  this.state.pendingAction === "checkLessonsLearned" ? /* @__PURE__ */ jsx("p", { children: "Do you want to save them before running the Lessons Learned check?" }) : this.state.pendingAction === "registerSequence" ? /* @__PURE__ */ jsx("p", { children: "Do you want to save them before registering the sequence?" }) : this.state.pendingAction && this.state.pendingAction.type === "designRtQpcrPrimers" ? /* @__PURE__ */ jsx("p", { children: "Do you want to save them before designing RT-qPCR primers?" }) : this.state.pendingAction && this.state.pendingAction.type === "exportFasta" ? /* @__PURE__ */ jsx("p", { children: "You have unsaved changes. The backend FASTA export will use the version currently on the server. Do you want to save before exporting?" }) : /* @__PURE__ */ jsxs("p", { children: [
                    "Do you want to save them before switching to",
                    " ",
                    /* @__PURE__ */ jsx("b", { children: this.state.pendingFilePath }),
                    "?"
                  ] })
                ] }),
                /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                  /* @__PURE__ */ jsx(Button, { onClick: this.handleUnsavedCancel, children: "Cancel" }),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      intent: Intent.DANGER,
                      onClick: this.handleUnsavedConfirm,
                      children: this.state.pendingAction === "checkLessonsLearned" ? "Discard & Check" : this.state.pendingAction === "registerSequence" ? "Discard & Register" : this.state.pendingAction && this.state.pendingAction.type === "designRtQpcrPrimers" ? "Discard & Design" : this.state.pendingAction && this.state.pendingAction.type === "exportFasta" ? "Discard & Export" : "Discard & Switch"
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      intent: Intent.PRIMARY,
                      onClick: this.handleUnsavedSave,
                      loading: this.state.isSaving,
                      children: this.state.pendingAction === "checkLessonsLearned" ? "Save & Check" : this.state.pendingAction === "registerSequence" ? "Save & Register" : this.state.pendingAction && this.state.pendingAction.type === "designRtQpcrPrimers" ? "Save & Design" : this.state.pendingAction && this.state.pendingAction.type === "exportFasta" ? "Save & Export" : "Save & Switch"
                    }
                  )
                ] }) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Alert,
            {
              isOpen: this.state.isRegisterConfirmOpen,
              cancelButtonText: "Cancel",
              confirmButtonText: "Register",
              icon: "confirm",
              intent: Intent.PRIMARY,
              onCancel: () => this.setState({ isRegisterConfirmOpen: false }),
              onConfirm: () => this.doRegisterSequence(),
              children: [
                /* @__PURE__ */ jsxs("p", { children: [
                  "Are you sure you want to register ",
                  /* @__PURE__ */ jsx("b", { children: this.state.currentFilePath }),
                  "?"
                ] }),
                /* @__PURE__ */ jsxs("p", { children: [
                  "This will assign it a registry ID such as ",
                  /* @__PURE__ */ jsx("b", { children: "pDTX1234" }),
                  " (circular) or ",
                  /* @__PURE__ */ jsx("b", { children: "sDTX1234" }),
                  " (linear) and rename the file. This action cannot be cleanly undone."
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Alert,
            {
              isOpen: this.state.isIdTakenAlertOpen,
              cancelButtonText: "Cancel",
              confirmButtonText: "Generate New ID",
              icon: "warning-sign",
              intent: Intent.WARNING,
              onCancel: () => this.setState({ isIdTakenAlertOpen: false }),
              onConfirm: () => this.doRegisterSequence(true),
              children: [
                /* @__PURE__ */ jsxs("p", { children: [
                  "The registry ID ",
                  /* @__PURE__ */ jsx("b", { children: this.state.attemptedId }),
                  " is already taken."
                ] }),
                /* @__PURE__ */ jsx("p", { children: "Would you like to generate a new ID instead? (This will cause your file to be renamed)." })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Dialog,
            {
              isOpen: this.state.isSaveAsDialogOpen,
              onClose: () => !this.state.isSavingAs && this.setState({ isSaveAsDialogOpen: false }),
              title: "Save As",
              icon: "floppy-disk",
              canOutsideClickClose: !this.state.isSavingAs,
              canEscapeKeyClose: !this.state.isSavingAs,
              children: [
                /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_BODY, children: [
                  /* @__PURE__ */ jsx("div", { style: { marginBottom: 20 }, children: /* @__PURE__ */ jsxs("label", { className: Classes.LABEL, children: [
                    "Folder",
                    /* @__PURE__ */ jsx(
                      FolderPicker,
                      {
                        value: this.state.saveAsFolder,
                        onChange: (val) => !this.state.isSavingAs && this.setState({ saveAsFolder: val })
                      }
                    )
                  ] }) }),
                  /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsxs("label", { className: Classes.LABEL, children: [
                    "Filename",
                    /* @__PURE__ */ jsx(
                      InputGroup,
                      {
                        fill: true,
                        disabled: this.state.isSavingAs,
                        value: this.state.saveAsFilename,
                        onChange: (e) => !this.state.isSavingAs && this.setState({ saveAsFilename: e.target.value }),
                        placeholder: "my_file.gb"
                      }
                    )
                  ] }) })
                ] }),
                /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      disabled: this.state.isSavingAs,
                      onClick: () => this.setState({ isSaveAsDialogOpen: false }),
                      children: "Cancel"
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      intent: Intent.PRIMARY,
                      onClick: this.onSaveAsConfirm,
                      loading: this.state.isSavingAs,
                      children: "Save"
                    }
                  )
                ] }) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Dialog,
            {
              isOpen: this.state.isBuildOutputDialogOpen,
              onClose: this.closeBuildOutputDialog,
              title: "Operation Results",
              icon: "build",
              style: { width: "90%", maxWidth: "1200px" },
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: Classes.DIALOG_BODY,
                    onKeyDown: (e) => {
                      if (e.key === "Enter") {
                        this.closeBuildOutputDialog();
                      }
                    },
                    children: /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: "20px", height: "500px" }, children: [
                      /* @__PURE__ */ jsxs(
                        "div",
                        {
                          style: {
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            minWidth: 0
                          },
                          children: [
                            /* @__PURE__ */ jsx("h4", { style: { marginTop: 0, marginBottom: 10 }, children: "Assembly Build" }),
                            /* @__PURE__ */ jsxs(
                              "pre",
                              {
                                style: {
                                  backgroundColor: "#f0f0f0",
                                  padding: "15px",
                                  borderRadius: "4px",
                                  flexGrow: 1,
                                  overflow: this.state.isRegistering ? "hidden" : "auto",
                                  whiteSpace: "pre-wrap",
                                  fontSize: "12px",
                                  fontFamily: "monospace",
                                  margin: 0,
                                  position: "relative",
                                  minHeight: "100px"
                                },
                                tabIndex: "0",
                                children: [
                                  this.state.isRegistering ? /* @__PURE__ */ jsx(
                                    "div",
                                    {
                                      style: {
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: "100%",
                                        width: "100%",
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        backgroundColor: "rgba(240, 240, 240, 0.7)",
                                        zIndex: 10
                                      },
                                      children: /* @__PURE__ */ jsx(Spinner, {})
                                    }
                                  ) : null,
                                  this.state.buildOutput && this.state.buildOutput.trim && this.state.buildOutput.trim().length > 0 ? this.renderHighlightedOutput(this.state.buildOutput) : "Everything is up to date."
                                ]
                              }
                            )
                          ]
                        }
                      ),
                      /* @__PURE__ */ jsxs(
                        "div",
                        {
                          style: {
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            minWidth: 0
                          },
                          children: [
                            /* @__PURE__ */ jsx("h4", { style: { marginTop: 0, marginBottom: 10 }, children: "Alignment Recalculation" }),
                            /* @__PURE__ */ jsx(
                              "pre",
                              {
                                style: {
                                  backgroundColor: "#f0f0f0",
                                  padding: "15px",
                                  borderRadius: "4px",
                                  flexGrow: 1,
                                  overflow: "auto",
                                  whiteSpace: "pre-wrap",
                                  fontSize: "12px",
                                  fontFamily: "monospace",
                                  margin: 0
                                },
                                tabIndex: "0",
                                children: this.state.alignmentOutput ? this.renderHighlightedOutput(this.state.alignmentOutput) : "No changes needed."
                              }
                            )
                          ]
                        }
                      )
                    ] })
                  }
                ),
                /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                  this.state.alignmentOutput && this.state.alignmentOutput.includes("[WARN]") && /* @__PURE__ */ jsx(
                    Button,
                    {
                      intent: Intent.DANGER,
                      onClick: this.handleCleanupFailedAlignments,
                      icon: "trash",
                      style: { marginRight: 10 },
                      children: "Cleanup Failed Alignments"
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      intent: Intent.PRIMARY,
                      onClick: this.closeBuildOutputDialog,
                      children: "Close"
                    }
                  )
                ] }) }),
                "          "
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Dialog,
            {
              isOpen: this.state.isLessonsLearnedDialogOpen,
              onClose: this.closeLessonsLearnedDialog,
              onOpened: this.handleDialogOpened,
              title: "Lessons Learned Check",
              icon: "info-sign",
              style: {
                width: this.state.lessonsLearnedDialogSize.width,
                height: this.state.lessonsLearnedDialogSize.height,
                maxWidth: "none",
                resize: "both",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column"
              },
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    ref: this.lessonsLearnedDialogRef,
                    className: Classes.DIALOG_BODY,
                    style: {
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden"
                    },
                    children: /* @__PURE__ */ jsxs(
                      "div",
                      {
                        style: {
                          position: "relative",
                          flexGrow: 1,
                          minHeight: "200px",
                          display: "flex",
                          flexDirection: "column"
                        },
                        children: [
                          this.state.isCheckingLessonsLearned && /* @__PURE__ */ jsx(
                            "div",
                            {
                              style: {
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 10
                              },
                              children: /* @__PURE__ */ jsx(Spinner, { size: 30 })
                            }
                          ),
                          /* @__PURE__ */ jsx(
                            "pre",
                            {
                              style: {
                                backgroundColor: "#f0f0f0",
                                padding: "15px",
                                borderRadius: "4px",
                                flexGrow: 1,
                                overflow: "auto",
                                whiteSpace: "pre-wrap",
                                fontSize: "12px",
                                fontFamily: "monospace",
                                opacity: this.state.isCheckingLessonsLearned ? 0.5 : 1,
                                margin: 0
                              },
                              children: this.renderClickableText(this.state.lessonsLearnedOutput)
                            }
                          )
                        ]
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: Intent.PRIMARY,
                    onClick: this.closeLessonsLearnedDialog,
                    children: "Close"
                  }
                ) }) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Dialog,
            {
              isOpen: this.state.isVerificationPrimersDialogOpen,
              onClose: this.closeVerificationPrimersDialog,
              onOpened: this.handleVerificationPrimersDialogOpened,
              title: /* @__PURE__ */ jsxs(
                "div",
                {
                  style: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    paddingRight: 10
                  },
                  children: [
                    /* @__PURE__ */ jsx("span", { children: "Verification Primers Report" }),
                    /* @__PURE__ */ jsx(
                      Button,
                      {
                        icon: "share",
                        text: "Open in New Tab",
                        intent: Intent.PRIMARY,
                        small: true,
                        style: { marginRight: 25 },
                        disabled: this.state.isGeneratingVerificationPrimers || !this.state.verificationPrimersOutput,
                        onClick: () => {
                          const blob = new Blob([this.state.verificationPrimersOutput], {
                            type: "text/html"
                          });
                          const url = URL.createObjectURL(blob);
                          window.open(url, "_blank");
                          this.closeVerificationPrimersDialog();
                        }
                      }
                    )
                  ]
                }
              ),
              className: "verification-primers-dialog",
              style: {
                width: this.state.verificationPrimersDialogSize.width,
                height: this.state.verificationPrimersDialogSize.height,
                maxWidth: "none",
                resize: "both",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column"
              },
              children: [
                /* @__PURE__ */ jsx("style", { children: `
              .verification-primers-dialog .bp3-dialog-header .bp3-button.bp3-dialog-close-button {
                margin-top: 5px !important;
                margin-right: 5px !important;
              }
            ` }),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    ref: this.verificationPrimersDialogRef,
                    className: Classes.DIALOG_BODY,
                    style: {
                      margin: 0,
                      padding: 0,
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      overflow: "hidden"
                    },
                    children: this.state.isGeneratingVerificationPrimers ? /* @__PURE__ */ jsxs(
                      "div",
                      {
                        style: {
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "white"
                        },
                        children: [
                          /* @__PURE__ */ jsx(Spinner, { size: 50 }),
                          /* @__PURE__ */ jsx("div", { style: { marginTop: 20, color: "#5c7080" }, children: "Generating Primers..." })
                        ]
                      }
                    ) : /* @__PURE__ */ jsx(
                      "iframe",
                      {
                        title: "Verification Primers Report",
                        srcDoc: this.state.verificationPrimersOutput,
                        style: {
                          width: "100%",
                          height: "100%",
                          border: "none",
                          flexGrow: 1
                        },
                        onLoad: (e) => {
                          try {
                            const iframeDoc = e.target.contentDocument || e.target.contentWindow.document;
                            iframeDoc.addEventListener("keydown", (event) => {
                              if (event.key === "Escape") {
                                this.closeVerificationPrimersDialog();
                              }
                            });
                          } catch (err) {
                            console.error("Failed to attach Esc key listener to iframe", err);
                          }
                        }
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: /* @__PURE__ */ jsx(
                  Button,
                  {
                    style: { marginTop: 5 },
                    onClick: this.closeVerificationPrimersDialog,
                    children: "Close"
                  }
                ) }) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Dialog,
            {
              isOpen: this.state.isRtQpcrPrimersDialogOpen,
              onClose: this.closeRtQpcrPrimersDialog,
              onOpened: this.handleRtQpcrPrimersDialogOpened,
              title: /* @__PURE__ */ jsxs(
                "div",
                {
                  style: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    paddingRight: 10
                  },
                  children: [
                    /* @__PURE__ */ jsx("span", { children: "RT-qPCR Primers Report" }),
                    /* @__PURE__ */ jsx(
                      Button,
                      {
                        icon: "share",
                        text: "Open in New Tab",
                        intent: Intent.PRIMARY,
                        small: true,
                        style: { marginRight: 25 },
                        disabled: this.state.isGeneratingRtQpcrPrimers || !this.state.rtQpcrPrimersOutput,
                        onClick: () => {
                          const blob = new Blob([this.state.rtQpcrPrimersOutput], {
                            type: "text/html"
                          });
                          const url = URL.createObjectURL(blob);
                          window.open(url, "_blank");
                          this.closeRtQpcrPrimersDialog();
                        }
                      }
                    )
                  ]
                }
              ),
              className: "rt-qpcr-primers-dialog",
              style: {
                width: this.state.rtQpcrPrimersDialogSize.width,
                height: this.state.rtQpcrPrimersDialogSize.height,
                maxWidth: "none",
                resize: "both",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column"
              },
              children: [
                /* @__PURE__ */ jsx("style", { children: `
              .rt-qpcr-primers-dialog .bp3-dialog-header .bp3-button.bp3-dialog-close-button {
                margin-top: 5px !important;
                margin-right: 5px !important;
              }
            ` }),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    ref: this.rtQpcrPrimersDialogRef,
                    className: Classes.DIALOG_BODY,
                    style: {
                      margin: 0,
                      padding: 0,
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      overflow: "hidden"
                    },
                    children: this.state.isGeneratingRtQpcrPrimers ? /* @__PURE__ */ jsxs(
                      "div",
                      {
                        style: {
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "white"
                        },
                        children: [
                          /* @__PURE__ */ jsx(Spinner, { size: 50 }),
                          /* @__PURE__ */ jsx("div", { style: { marginTop: 20, color: "#5c7080" }, children: "Designing RT-qPCR Primers..." })
                        ]
                      }
                    ) : /* @__PURE__ */ jsx(
                      "iframe",
                      {
                        title: "RT-qPCR Primers Report",
                        srcDoc: this.state.rtQpcrPrimersOutput,
                        style: {
                          width: "100%",
                          height: "100%",
                          border: "none",
                          flexGrow: 1
                        },
                        onLoad: (e) => {
                          try {
                            const iframeDoc = e.target.contentDocument || e.target.contentWindow.document;
                            iframeDoc.addEventListener("keydown", (event) => {
                              if (event.key === "Escape") {
                                this.closeRtQpcrPrimersDialog();
                              }
                            });
                          } catch (err) {
                            console.error("Failed to attach Esc key listener to iframe", err);
                          }
                        }
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: /* @__PURE__ */ jsx(
                  Button,
                  {
                    style: { marginTop: 5 },
                    onClick: this.closeRtQpcrPrimersDialog,
                    children: "Close"
                  }
                ) }) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Dialog,
            {
              isOpen: this.state.isSaveAsPrimerDialogOpen,
              "data-testid": "save-as-primer-dialog",
              onClose: () => !this.state.isSavingAsPrimer && this.setState({ isSaveAsPrimerDialogOpen: false }),
              canOutsideClickClose: !this.state.isSavingAsPrimer,
              canEscapeKeyClose: !this.state.isSavingAsPrimer,
              title: "Save as Primer",
              icon: "floppy-disk",
              children: [
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: Classes.DIALOG_BODY,
                    onKeyDown: (e) => {
                      if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && !this.state.isSavingAsPrimer) {
                        this.onSaveAsPrimerConfirm();
                      }
                    },
                    children: [
                      /* @__PURE__ */ jsx("div", { style: { marginBottom: 20 }, children: /* @__PURE__ */ jsxs("label", { className: Classes.LABEL, children: [
                        "Folder",
                        /* @__PURE__ */ jsx(
                          FolderPicker,
                          {
                            value: this.state.saveAsPrimerFolder,
                            onChange: (val) => this.setState({ saveAsPrimerFolder: val })
                          }
                        )
                      ] }) }),
                      /* @__PURE__ */ jsx("div", { style: { marginBottom: 20 }, children: /* @__PURE__ */ jsxs("label", { className: Classes.LABEL, children: [
                        "Filename",
                        /* @__PURE__ */ jsx(
                          InputGroup,
                          {
                            fill: true,
                            autoFocus: true,
                            value: this.state.saveAsPrimerFilename,
                            onChange: (e) => this.setState({ saveAsPrimerFilename: e.target.value }),
                            placeholder: "primer.gb"
                          }
                        )
                      ] }) }),
                      /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsxs("label", { className: Classes.LABEL, children: [
                        "Sequence",
                        /* @__PURE__ */ jsx(
                          InputGroup,
                          {
                            fill: true,
                            disabled: true,
                            readOnly: true,
                            value: this.state.saveAsPrimerSequence
                          }
                        )
                      ] }) })
                    ]
                  }
                ),
                /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      disabled: this.state.isSavingAsPrimer,
                      onClick: () => this.setState({ isSaveAsPrimerDialogOpen: false }),
                      children: "Cancel"
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      intent: Intent.PRIMARY,
                      loading: this.state.isSavingAsPrimer,
                      onClick: this.onSaveAsPrimerConfirm,
                      children: "Save Primer"
                    }
                  )
                ] }) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Dialog,
            {
              isOpen: this.state.isExtractDialogOpen,
              onOpened: this.handleExtractOpened,
              onClose: () => !this.state.isExtracting && this.setState({ isExtractDialogOpen: false }),
              canOutsideClickClose: !this.state.isExtracting,
              canEscapeKeyClose: !this.state.isExtracting,
              title: "Extract Sequence",
              icon: "export",
              children: [
                /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_BODY, children: [
                  /* @__PURE__ */ jsx("div", { style: { marginBottom: 20 }, children: /* @__PURE__ */ jsxs("label", { className: Classes.LABEL, children: [
                    "Folder",
                    /* @__PURE__ */ jsx(
                      FolderPicker,
                      {
                        value: this.state.extractFolder,
                        onChange: (val) => this.setState({ extractFolder: val })
                      }
                    )
                  ] }) }),
                  /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsxs("label", { className: Classes.LABEL, children: [
                    "Filename",
                    /* @__PURE__ */ jsx(
                      InputGroup,
                      {
                        fill: true,
                        inputRef: this.extractInputRef,
                        value: this.state.extractFilename,
                        onKeyDown: this.handleExtractKeyDown,
                        onChange: (e) => this.setState({ extractFilename: e.target.value }),
                        placeholder: "extracted_sequence.gb"
                      }
                    )
                  ] }) })
                ] }),
                /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      disabled: this.state.isExtracting,
                      onClick: () => this.setState({ isExtractDialogOpen: false }),
                      children: "Cancel"
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      intent: Intent.PRIMARY,
                      loading: this.state.isExtracting,
                      onClick: this.onExtractConfirm,
                      children: "Extract"
                    }
                  )
                ] }) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            Alert,
            {
              isOpen: this.state.backendDown,
              icon: "error",
              intent: Intent.DANGER,
              confirmButtonText: "Retry",
              onConfirm: () => {
                this.setState({ backendDown: false });
                this.checkBackendHeartbeat();
              },
              canEscapeKeyCancel: false,
              canOutsideClickCancel: false,
              children: [
                /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsx("strong", { children: "Cannot reach the Python backend." }) }),
                /* @__PURE__ */ jsxs("p", { children: [
                  "The server at ",
                  /* @__PURE__ */ jsx("code", { children: "/api/heartbeat" }),
                  " did not respond. File operations and auto-annotation will not work until the backend is running."
                ] }),
                /* @__PURE__ */ jsxs("p", { children: [
                  "Start the backend with ",
                  /* @__PURE__ */ jsx("code", { children: "bash bin/start.sh" }),
                  " or",
                  " ",
                  /* @__PURE__ */ jsx("code", { children: "bash bin/restart.sh" }),
                  ", then click Retry."
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsx(BackgroundBuildIndicator, {})
        ]
      }
    ) });
  }
}
